import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "wouter";
import { groupPeriodsForReceipts, groupPlanLabel } from "@/lib/period-grouping";
import { periodMoney, sumPeriodMoney } from "@/lib/period-money";
import {
  useGetAccount,
  useListAccountMemberships,
  useListMemberCharges,
  useUpdateMemberCharge,
  getListMemberChargesQueryKey,
  useListStockOut,
  useListInventoryItems,
  useListInventoryCategories,
  useListAccountContacts,
  useUpdateAccountMembership,
  useDeleteAccountMembership,
  useListInventoryPlans,
  useGetAccountProforma,
  useGetAccountOrderReceipt,
  useSendAccountOrderReceipt,
  useGetAccountPlanSchedule,
  useMarkPlanPeriodPaid,
  useGenerateBundleReceipt,
  useGenerateBundleInvoice,
  useUpdatePlanPeriodReminder,
  useGeneratePlanPeriodReceipt,
  useGenerateMemberChargeReceipt,
  useUpdatePeriodAmount,
  useGetAccountDeposits,
  useApplyDepositToPeriod,
  useListAccountCreditNotes,
  useApplyCreditNoteToPeriod,
  getListAccountCreditNotesQueryKey,
  useGetAccountRefunds,
  getGetAccountRefundsQueryKey,
  getGetAccountProformaQueryKey,
  getGetAccountOrderReceiptQueryKey,
  getGetAccountPlanScheduleQueryKey,
  getListAccountMembershipsQueryKey,
  getListStockOutQueryKey,
  // useSendAccountProformaReceipt removed — receipt is now account-level (monthly).
  type Membership,
  type MemberCharge,
  type StockOut,
  type InventoryItem,
  type InventoryPlan,
  type PlanSchedulePeriod,
  type PlanPayment,
  type GeneratePlanPeriodReceiptBody,
  type GenerateMemberChargeReceiptBody,
  type Refund,
  type HeldDeposit,
  type CreditNote,
} from "@workspace/api-client-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { gstTreatmentLabel } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { uploadPaymentProof } from "@/lib/upload";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Pencil,
  GitMerge,
  CheckCircle2,
  AlertCircle,
  CalendarPlus,
  Plus,
  Loader2,
  Send,
  ExternalLink,
  Trash2,
  Download,
  LayoutGrid,
  Presentation,
  PlusCircle,
  Coffee,
  Printer,
  PenLine,
  Boxes,
  BadgeCheck,
  Eye,
  Mail,
  MessageCircle,
  Upload,
  RefreshCw,
  Receipt,
  Ticket,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NewBookingSheet } from "@/components/accounts/new-booking-sheet";
import { BookingsList } from "@/components/accounts/bookings-list";
import { AccountForm } from "@/components/accounts/account-form";
import { ContactsList } from "@/components/accounts/contacts-list";
import { DocumentsList } from "@/components/accounts/documents-list";
import { AttendanceList } from "@/components/attendance/attendance-list";
import { CommunicationTab } from "@/components/accounts/communication-tab";
import { MergeDialog } from "@/components/accounts/merge-dialog";
import { MarkStockOutPaidDialog } from "@/components/operations/mark-stock-out-paid-dialog";
import { ViewStockOutReceiptDialog } from "@/components/operations/view-stock-out-receipt-dialog";
// Phase 36 — Bundle billing dialogs (composite-plan CRM surface).
import { RestructureBundleDialog } from "@/components/billing/restructure-bundle-dialog";
import { CreditNotesList } from "@/components/billing/credit-notes-list";
import { SecurityDepositsList } from "@/components/billing/security-deposits-list";
import { ManualCreditNoteDialog } from "@/components/billing/manual-credit-note-dialog";
import { ExitPlanDialog } from "@/components/plan-exit/exit-plan-dialog";
// Phase 38 — RecordPaymentDialog extracted to a shared module so the Member
// profile's Collect-payment surface can reuse it (account-detail.tsx's own
// usage below is unchanged; it never passes the new additionalPeriods prop).
import { RecordPaymentDialog } from "@/components/billing/record-payment-dialog";
// Phase 39 — ChargeRecordPaymentDialog extracted to a shared module so the
// Member profile's charge-collection surfaces can reuse it (account-detail.tsx's
// own 3 call sites below are unchanged; they never pass the new
// additionalCharges prop).
import { ChargeRecordPaymentDialog } from "@/components/billing/charge-record-payment-dialog";
import { format } from "date-fns";
import { formatOrderReceiptDisplay } from "@workspace/billing";
import { apiErrorDetail } from "@/lib/period-payable";

// Direct authenticated DELETE for a Pay-Per-Use stock-out.
//
// We intentionally do NOT add an orval hook / openapi path for this endpoint:
// a concurrent session is editing openapi.yaml and re-running codegen would
// collide. Instead we replicate exactly what the orval mutator (customFetch in
// @workspace/api-client-react) does for auth + base URL — Bearer token from
// localStorage("crm_token") and the same VITE_API_BASE/PROD base resolution as
// src/main.tsx — and call fetch() directly. Mirrors getListStockOutQueryKey's
// `/api/operations/stock-out` path so the URL stays in lockstep with the client.
class StockOutDeleteError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
  ) {
    super(`stock-out delete failed: ${status} ${code ?? ""}`.trim());
    this.name = "StockOutDeleteError";
  }
}

async function deleteStockOutDirect(id: number): Promise<void> {
  // Same base-URL resolution as src/main.tsx setBaseUrl(): VITE_API_BASE wins,
  // else the hardcoded prod host in prod builds, else relative (dev proxy).
  const apiBase: string =
    import.meta.env.VITE_API_BASE ??
    (import.meta.env.PROD ? "https://de2uveuyxp2cc.cloudfront.net" : "");
  const base = apiBase.replace(/\/+$/, "");
  const token = localStorage.getItem("crm_token");
  const res = await fetch(`${base}/api/operations/stock-out/${id}`, {
    method: "DELETE",
    headers: {
      accept: "application/json, application/problem+json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    let code: string | null = null;
    try {
      const body = (await res.json()) as { code?: string };
      code = typeof body?.code === "string" ? body.code : null;
    } catch {
      /* non-JSON error body */
    }
    throw new StockOutDeleteError(res.status, code);
  }
}

/**
 * 2026-06-10 slice 3 — Account detail redesign per operator UAT.
 *
 * Layout:
 *   • Basic Details → Accordion (collapsible; was a static header card).
 *     Action buttons (New Booking, Edit, Merge) stay at the top alongside
 *     the legalName so they're reachable without opening the accordion.
 *   • Tabs renamed to match operator vocabulary:
 *       Contacts            → Members
 *       Memberships         → Plan
 *       Documents           → Documents (unchanged)
 *       Charges & Invoices  → Transactions  (grouped by category)
 *     + NEW Visitors tab (placeholder — no schema yet; see below).
 *
 * Transactions categorization:
 *   Source data: member_charges.source_type.
 *   Today's values in the DB: Plan_Rental, Daily_Booking, Hourly_Booking,
 *   pay_per_use, Late_Fee. The operator's target categories were Plan,
 *   F&B, Add-ons, Conference Room, Prints, Stationary, Miscellaneous.
 *   For now we group existing source_types under the closest target
 *   category and leave the unused buckets empty (F&B / Prints /
 *   Stationary will populate when those modules wire transactions —
 *   none does today, so the empty group renders a "no transactions"
 *   line rather than nothing).
 *
 * Visitors tab: there is no visitors table or hook in the schema yet.
 * Stubbed with a "coming soon" panel — wiring a real list needs a
 * migration + API surface + UI (its own slice).
 */

// ── Helpers ────────────────────────────────────────────────────────────────

/** INR formatter. */
function formatINR(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(amount)}`;
}

/**
 * Map member_charges.source_type to the operator-visible category.
 * Anything unmapped lands under "Miscellaneous" so unknown source_types
 * surface in the UI rather than silently disappearing.
 */
const CATEGORY_LABELS = [
  "Plan",
  "Booking",
  "Conference Room",
  "Add-ons",
  "F&B",
  "Prints",
  "Stationary",
  "Miscellaneous",
] as const;
type Category = (typeof CATEGORY_LABELS)[number];

// Sidebar icons per category — mirrors the Stock-In tab's icon rail.
const CATEGORY_ICONS: Record<Category, typeof LayoutGrid> = {
  Plan: LayoutGrid,
  Booking: Ticket,
  "Conference Room": Presentation,
  "Add-ons": PlusCircle,
  "F&B": Coffee,
  Prints: Printer,
  Stationary: PenLine,
  Miscellaneous: Boxes,
};

function categoryForSourceType(sourceType: string): Category {
  switch (sourceType) {
    case "Plan_Rental":
      return "Plan";
    // Day Pass + room (hourly) bookings are one-time sales → their own
    // Booking category where they can be collected (marked Paid).
    case "Daily_Booking":
    case "Hourly_Booking":
      return "Booking";
    case "Conference_Room":
      return "Conference Room";
    case "Meeting_Room":
      return "Conference Room";
    case "pay_per_use":
      return "Add-ons";
    case "F_AND_B":
      return "F&B";
    case "Print":
      return "Prints";
    case "Stationary":
      return "Stationary";
    case "Late_Fee":
      return "Miscellaneous";
    default:
      return "Miscellaneous";
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────

// Today in IST as 'YYYY-MM-DD'. Membership endDate is a date column
// ('YYYY-MM-DD'), so a lexical string compare is also a chronological one.
function todayYmd(): string {
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

// A plan whose end date has already passed. Post-end, the exit is settled:
// the Credit Note and Reactivate CTAs are hidden (nothing left to credit or
// undo). A null endDate (legacy exits, open-ended plans) is never "past".
function isPastEnd(m: Membership): boolean {
  return !!m.endDate && m.endDate.slice(0, 10) < todayYmd();
}

function MembershipsList({ accountId }: { accountId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: memberships, isLoading } = useListAccountMemberships(accountId);
  const { data: plans = [] } = useListInventoryPlans();
  const updateMembership = useUpdateAccountMembership();
  const deleteMembership = useDeleteAccountMembership();
  const [, navigate] = useLocation();
  const [proformaId, setProformaId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Membership | null>(null);
  // Whole-deal delete confirm target — holds the full group array so the
  // confirm dialog can name every plan being removed.
  const [deleteGroup, setDeleteGroup] = useState<Membership[] | null>(null);
  // Manual credit-note dialog target — set when operator clicks Credit Note
  // on an exited plan row. The dialog handles amount + reason input and the
  // POST /accounts/:id/credit-notes call.
  const [creditNoteTarget, setCreditNoteTarget] = useState<Membership | null>(null);
  // Exit-date dialog target — the memberships to soft-exit (one for a solo row,
  // the active lines for a deal). Exit now always asks for a date.
  const [exitTarget, setExitTarget] = useState<Membership[] | null>(null);
  const [exitIsDeal, setExitIsDeal] = useState(false);

  // Phase 36 — Bundle billing dialogs. Fetch plan-schedule + charges so we
  // can pass period/charge lists into the bundle dialogs.
  const { data: planSchedule } = useGetAccountPlanSchedule(accountId);
  const { data: chargesData } = useListMemberCharges({ memberId: accountId, pageSize: 200 });

  // Phase 36.1 — Held deposits for the account-wide Restructure dialog.
  const { data: deposits } = useGetAccountDeposits(accountId);
  // Phase 36.1 — Refunds for the read-only Refunds section on the Plan tab.
  const { data: refundsData } = useGetAccountRefunds(accountId);

  // Phase 36.1: Restructure is now account-wide (single boolean), not per-group.
  const [restructureOpen, setRestructureOpen] = useState<boolean>(false);

  // A multi-space-type deal (shared quote_group, added together) renders as ONE
  // collapsed line item; the chevron expands the per-space-type rows.
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());
  function toggleDeal(key: string) {
    setExpandedDeals((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Resolve each membership's planId → the real plan so the table shows the
  // plan name (Workstation, Private Suite, …) operators recognize from the
  // Plans page, not an opaque "PLN-2".
  const planById = useMemo(() => {
    const map: Record<number, InventoryPlan> = {};
    for (const p of plans) map[p.id] = p;
    return map;
  }, [plans]);

  // Group memberships created together in one Add Plan (shared quote_group_id) so
  // a multi-space-type deal reads as one plan instead of separate line items.
  // Each entry: [groupKey, memberships[]]. Solo memberships are their own group.
  const membershipGroups = useMemo(() => {
    const map = new Map<string, Membership[]>();
    for (const m of (memberships ?? []) as Membership[]) {
      const key = m.quoteGroupId ?? `solo-${m.id}`;
      (map.get(key) ?? map.set(key, []).get(key)!).push(m);
    }
    return Array.from(map.entries());
  }, [memberships]);

  // A plan with any recorded payment must not be deletable — deleting it would
  // orphan its payment/receipt records. Operators use Exit instead. Threshold is
  // ANY amountPaid > 0 across the plan's schedule periods (partial payments count).
  // UI-level guard only; the server delete endpoint is unchanged.
  const paidMembershipIds = useMemo(
    () =>
      new Set<number>(
        (planSchedule?.periods ?? [])
          .filter((p) => Number(p.amountPaid ?? 0) > 0)
          .map((p) => p.membershipId),
      ),
    [planSchedule],
  );

  // Exit → open the date dialog (below); Reactivate flips straight back to
  // active with no date. The exit-date write + member deactivation cascade all
  // happen server-side off `status: 'exited'` + `endDate`.
  function openExit(targets: Membership[], isDeal: boolean) {
    setExitTarget(targets);
    setExitIsDeal(isDeal);
  }

  // Confirm from the exit dialog — soft-exit each target SEQUENTIALLY (so the
  // backend's "last active plan?" probe sees prior lines already committed and
  // the final line correctly deactivates the member), stamping the chosen exit
  // date as the end date.
  async function confirmExit(exitDate: string) {
    if (!exitTarget) return;
    const isDeal = exitIsDeal;
    try {
      for (const m of exitTarget) {
        await updateMembership.mutateAsync({
          id: accountId,
          membershipId: m.id,
          data: { status: "exited", endDate: exitDate },
        });
      }
      toast({ title: isDeal ? "Deal exited." : "Membership exited." });
      queryClient.invalidateQueries({
        queryKey: getListAccountMembershipsQueryKey(accountId),
      });
      setExitTarget(null);
    } catch {
      toast({ title: "Failed to update membership.", variant: "destructive" });
    }
  }

  async function handleReactivate(membership: Membership) {
    try {
      await updateMembership.mutateAsync({
        id: accountId,
        membershipId: membership.id,
        data: { status: "active" },
      });
      toast({ title: "Membership reactivated." });
      queryClient.invalidateQueries({
        queryKey: getListAccountMembershipsQueryKey(accountId),
      });
    } catch {
      toast({ title: "Failed to update membership.", variant: "destructive" });
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMembership.mutateAsync({ id: accountId, membershipId: deleteTarget.id });
      toast({ title: "Plan deleted." });
      queryClient.invalidateQueries({
        queryKey: getListAccountMembershipsQueryKey(accountId),
      });
      setDeleteTarget(null);
    } catch {
      toast({ title: "Failed to delete plan.", variant: "destructive" });
    }
  }

  // Group-level Reactivate — flips every line in a deal back to active. The
  // exit direction goes through the date dialog instead (openExit).
  async function handleGroupReactivate(group: Membership[]) {
    try {
      for (const m of group) {
        await updateMembership.mutateAsync({
          id: accountId,
          membershipId: m.id,
          data: { status: "active" },
        });
      }
      toast({ title: "Deal reactivated." });
      queryClient.invalidateQueries({
        queryKey: getListAccountMembershipsQueryKey(accountId),
      });
    } catch {
      toast({ title: "Failed to update deal.", variant: "destructive" });
    }
  }

  // Group-level Delete — removes every membership in the deal. Destructive,
  // gated behind the whole-deal confirm dialog (setDeleteGroup).
  async function handleGroupDelete() {
    if (!deleteGroup) return;
    try {
      for (const m of deleteGroup) {
        await deleteMembership.mutateAsync({ id: accountId, membershipId: m.id });
      }
      toast({ title: "Deal deleted." });
      queryClient.invalidateQueries({
        queryKey: getListAccountMembershipsQueryKey(accountId),
      });
      setDeleteGroup(null);
    } catch {
      toast({ title: "Failed to delete deal.", variant: "destructive" });
    }
  }

  // Phase 36.1: active memberships gate for the top-level Restructure button.
  const activeMemberships = (memberships ?? []).filter((m) => m.status === "active");

  // One per-space-type row. Reused for solo plans and for the expanded
  // children of a collapsed Plan-deal summary row (indented when a child).
  function renderMembershipRow(m: Membership, indented: boolean) {
    return (
      <TableRow key={m.id} className={indented ? "[&>td:first-child]:pl-10" : ""}>
        <TableCell>
          {planById[m.planId] ? (
            <div className="flex flex-col">
              {/* An expanded deal CHILD (indented) shows only its space type —
                  no individual "Plan #{id}" (the deal's summary row carries the
                  id). A standalone plan keeps "Plan #{id}". */}
              {indented ? (
                <>
                  <span className="text-sm font-medium">
                    {m.quantity && m.quantity > 1 ? `${m.quantity} × ` : ""}
                    {planById[m.planId]!.planName.replace(/_/g, " ")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {m.durationMonths ? `${m.durationMonths} mo` : ""}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium">Plan #{m.id}</span>
                  <span className="text-xs text-muted-foreground">
                    {m.quantity && m.quantity > 1 ? `${m.quantity}× ` : ""}
                    {planById[m.planId]!.planName.replace(/_/g, " ")}
                    {m.durationMonths ? ` · ${m.durationMonths} mo` : ""}
                  </span>
                </>
              )}
            </div>
          ) : (
            <span className="font-mono text-xs">PLN-{m.planId}</span>
          )}
        </TableCell>
        <TableCell>
          {m.startDate ? format(new Date(m.startDate), "MMM d, yyyy") : "—"}
        </TableCell>
        <TableCell>
          {m.endDate ? format(new Date(m.endDate), "MMM d, yyyy") : "—"}
        </TableCell>
        <TableCell>{m.cycleMode?.replace(/_/g, " ") ?? "—"}</TableCell>
        <TableCell>{m.billingCycleDay ?? "—"}</TableCell>
        <TableCell>
          <Badge variant={m.status === "active" ? "default" : "secondary"}>
            {m.status === "active" ? "Active" : "Deactivated"}
          </Badge>
        </TableCell>
        <TableCell>
          {!indented && (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/accounts/${accountId}/plans/${m.id}`)}
              >
                View
              </Button>
              {/* Exit asks for a date; Reactivate flips back instantly but is
                  hidden once the end date has passed (settled exit). */}
              {m.status === "active" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openExit([m], false)}
                  disabled={updateMembership.isPending}
                >
                  Exit
                </Button>
              ) : (
                !isPastEnd(m) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReactivate(m)}
                    disabled={updateMembership.isPending}
                  >
                    Reactivate
                  </Button>
                )
              )}
              {/* Manual credit-note CTA — shown on exited rows whose end date
                  hasn't passed yet. Lets the operator issue a §34 GST credit
                  note against the latest paid invoice for an early-exit
                  settlement. Amount entered manually. Hidden post-end-date:
                  a fully-elapsed term has nothing left to credit. */}
              {m.status === "exited" && !isPastEnd(m) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCreditNoteTarget(m)}
                >
                  Credit Note
                </Button>
              )}
              {!paidMembershipIds.has(m.id) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(m)}
                  aria-label="Delete plan"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-end gap-2">
        {activeMemberships.length >= 2 && (
          <Button size="sm" variant="outline" onClick={() => setRestructureOpen(true)}>
            Restructure
          </Button>
        )}
        <Button size="sm" onClick={() => navigate(`/accounts/${accountId}/add-plan`)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Plan
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded" />
          ))}
        </div>
      ) : !memberships || memberships.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No plans attached to this account.
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Cycle Mode</TableHead>
                <TableHead>Billing Day</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {membershipGroups.map(([groupKey, group]) => {
                // Solo plan → a normal single row.
                if (group.length <= 1) {
                  return (
                    <Fragment key={groupKey}>
                      {renderMembershipRow(group[0]!, false)}
                    </Fragment>
                  );
                }

                // Multi-space-type deal (added together under one quote_group) →
                // ONE collapsed line item. The chevron expands the per-space-type
                // rows; group-level actions live on the summary row itself.
                const isExpanded = expandedDeals.has(groupKey);
                const isBundle = !groupKey.startsWith("solo-");
                const spaceTypes = group
                  .map(
                    (m) =>
                      `${m.quantity && m.quantity > 1 ? `${m.quantity}× ` : ""}${
                        planById[m.planId]?.planName.replace(/_/g, " ") ?? `PLN-${m.planId}`
                      }${m.durationMonths ? ` · ${m.durationMonths} mo` : ""}`,
                  )
                  .join(" | ");
                const sameEnd = new Set(group.map((m) => m.endDate ?? "")).size === 1;
                const sameCycle = new Set(group.map((m) => m.cycleMode ?? "")).size === 1;
                const sameBilling =
                  new Set(group.map((m) => m.billingCycleDay ?? null)).size === 1;
                const statusUniform = new Set(group.map((m) => m.status)).size === 1;
                const endLabel = sameEnd
                  ? group[0]!.endDate
                    ? format(new Date(group[0]!.endDate), "MMM d, yyyy")
                    : "—"
                  : "Mixed";
                const cycleLabel = sameCycle
                  ? group[0]!.cycleMode?.replace(/_/g, " ") ?? "—"
                  : "Mixed";
                const billingLabel = sameBilling ? group[0]!.billingCycleDay ?? "—" : "—";
                const anyActive = group.some((m) => m.status === "active");
                // Deal fully past its end date → hide Reactivate (settled exit).
                const groupPastEnd = group.every((m) => isPastEnd(m));

                return (
                  <Fragment key={groupKey}>
                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="flex items-center gap-1 text-sm font-medium">
                            Plan #{group[0]!.id}
                            <button
                              type="button"
                              onClick={() => toggleDeal(groupKey)}
                              className="p-0.5 rounded hover:bg-muted"
                              aria-label={isExpanded ? "Collapse deal" : "Expand deal"}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </span>
                          <span className="text-xs text-muted-foreground">{spaceTypes}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {group[0]!.startDate
                          ? format(new Date(group[0]!.startDate), "MMM d, yyyy")
                          : "—"}
                      </TableCell>
                      <TableCell>{endLabel}</TableCell>
                      <TableCell>{cycleLabel}</TableCell>
                      <TableCell>{billingLabel}</TableCell>
                      <TableCell>
                        {statusUniform ? (
                          <Badge variant={group[0]!.status === "active" ? "default" : "secondary"}>
                            {group[0]!.status === "active" ? "Active" : "Deactivated"}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Mixed</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {isBundle ? (
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/accounts/${accountId}/plans/${group[0]!.id}`)}
                            >
                              View
                            </Button>
                            {anyActive ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={updateMembership.isPending}
                                onClick={() =>
                                  openExit(
                                    group.filter((m) => m.status === "active"),
                                    true,
                                  )
                                }
                              >
                                Exit
                              </Button>
                            ) : (
                              !groupPastEnd && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={updateMembership.isPending}
                                  onClick={() => handleGroupReactivate(group)}
                                >
                                  Reactivate
                                </Button>
                              )
                            )}
                            {!group.some((m) => paidMembershipIds.has(m.id)) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                aria-label="Delete deal"
                                onClick={() => setDeleteGroup(group)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {isExpanded && group.map((m) => renderMembershipRow(m, true))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <ProformaDialog
        accountId={accountId}
        proformaId={proformaId}
        onClose={() => setProformaId(null)}
      />

      <AlertDialog
        open={deleteTarget != null}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this plan?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes{" "}
              <span className="font-medium">
                {deleteTarget && planById[deleteTarget.planId]
                  ? planById[deleteTarget.planId]!.planName.replace(/_/g, " ")
                  : "this plan"}
              </span>{" "}
              from the account and stops its recurring billing. Past invoices are
              kept. This can&apos;t be undone — use &ldquo;Exit&rdquo; instead to keep the record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMembership.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleteMembership.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMembership.isPending ? "Deleting…" : "Delete plan"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Whole-deal delete confirm — removes EVERY line in a multi-space-type
          Plan deal at once. Distinct from the single-plan dialog above. */}
      <AlertDialog
        open={deleteGroup != null}
        onOpenChange={(o) => {
          if (!o) setDeleteGroup(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entire deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes all{" "}
              <span className="font-medium">{deleteGroup?.length ?? 0} plans</span> in
              this deal
              {deleteGroup && deleteGroup.length > 0 ? (
                <>
                  {" "}
                  (
                  {deleteGroup
                    .map((m) => planById[m.planId]?.planName.replace(/_/g, " "))
                    .filter(Boolean)
                    .join(", ")}
                  )
                </>
              ) : null}{" "}
              from the account and stops their recurring billing. Past invoices
              are kept. This can&apos;t be undone — use &ldquo;Exit&rdquo;
              instead to keep the records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMembership.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleGroupDelete();
              }}
              disabled={deleteMembership.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMembership.isPending
                ? "Deleting…"
                : `Delete ${deleteGroup?.length ?? 0} plans`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual credit-note dialog — operator types amount + reason. Backend
          splits taxable + GST at the plan rate, references the latest paid
          invoice, generates the §34 credit-note PDF. */}
      <ManualCreditNoteDialog
        accountId={accountId}
        membership={creditNoteTarget}
        planName={
          creditNoteTarget && planById[creditNoteTarget.planId]
            ? planById[creditNoteTarget.planId]!.planName.replace(/_/g, " ")
            : null
        }
        open={creditNoteTarget != null}
        onOpenChange={(o) => { if (!o) setCreditNoteTarget(null); }}
      />

      {/* Exit-date dialog — soft-exits the plan/deal as of the chosen date and
          triggers the member-deactivation cascade server-side. */}
      <ExitPlanDialog
        open={exitTarget != null}
        onOpenChange={(o) => { if (!o) setExitTarget(null); }}
        onConfirm={confirmExit}
        isPending={updateMembership.isPending}
        isDeal={exitIsDeal}
      />

      {/* Phase 36.1 — Account-wide Restructure dialog. Rendered once at
          component root; allActiveMemberships covers all groups so the operator
          can pick ANY combination of active recurring memberships to restructure.
          heldDeposits sourced from useGetAccountDeposits so the deposit picker
          shows the account's Held security deposits.
          On close, invalidate the refunds query so the Refunds section below
          reflects any refunds created by the restructure. */}
      <RestructureBundleDialog
        accountId={accountId}
        allActiveMemberships={memberships ?? []}
        heldDeposits={deposits?.items ?? []}
        periods={planSchedule?.periods ?? []}
        open={restructureOpen}
        onOpenChange={(v) => {
          setRestructureOpen(v);
          if (!v) {
            queryClient.invalidateQueries({
              queryKey: getGetAccountRefundsQueryKey(accountId),
            });
          }
        }}
      />

      {/* Phase 36.1 — Refunds section: read-only list of refunds created by
          restructure or deposit-refund flows for this account. */}
      {(() => {
        const refundItems: Refund[] = refundsData?.items ?? [];
        if (refundItems.length === 0) return null;
        return (
          <div className="space-y-2 pt-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Refunds</h3>
              <span className="text-xs text-muted-foreground">({refundItems.length})</span>
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Proof</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {refundItems.map((r: Refund) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(r.refundDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.creditNoteNumber
                          ? r.creditNoteNumber
                          : r.depositId != null
                            ? `Deposit #${r.depositId}`
                            : "—"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatINR(Number(r.amount))}
                      </TableCell>
                      <TableCell className="text-xs">{r.paymentMode}</TableCell>
                      <TableCell>
                        <Badge
                          variant={r.status === "Paid" ? "default" : "secondary"}
                          className="capitalize text-[10px]"
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.proofUrl ? (
                          <a
                            href={r.proofUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline"
                          >
                            View
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/** Read-only proforma ESTIMATE viewer (not a tax invoice). */
function ProformaDialog({
  accountId,
  proformaId,
  onClose,
}: {
  accountId: number;
  proformaId: number | null;
  onClose: () => void;
}) {
  const { data: proforma, isLoading } = useGetAccountProforma(
    accountId,
    proformaId ?? 0,
    {
      query: {
        enabled: proformaId != null,
        queryKey: getGetAccountProformaQueryKey(accountId, proformaId ?? 0),
      },
    },
  );

  const intraState =
    proforma != null && Number(proforma.igstTotal ?? 0) === 0;

  return (
    <Dialog open={proformaId != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Proforma Estimate{proforma ? ` · ${proforma.quoteNumber}` : ""}
          </DialogTitle>
          <DialogDescription>
            Estimate for this deal — not a tax invoice. Send the order receipt
            from the account's <span className="font-medium">Order Receipt</span>{" "}
            button (one per account per month).
          </DialogDescription>
        </DialogHeader>

        {isLoading || !proforma ? (
          <div className="space-y-2 py-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full rounded" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Months</TableHead>
                    <TableHead className="text-right">Line total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(proforma.lines ?? []).map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-sm">{l.description}</TableCell>
                      <TableCell className="text-right text-sm">{l.quantity}</TableCell>
                      <TableCell className="text-right text-sm">
                        {l.durationMonths ?? "—"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatINR(Number(l.lineTotal))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="ml-auto w-full max-w-xs space-y-0.5 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>{formatINR(Number(proforma.subtotal ?? 0))}</span>
              </div>
              {Number(proforma.totalDiscount ?? 0) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Discount</span>
                  <span>−{formatINR(Number(proforma.totalDiscount))}</span>
                </div>
              )}
              {proforma.gstApplicable && intraState && (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>CGST</span>
                    <span>{formatINR(Number(proforma.cgstTotal ?? 0))}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>SGST</span>
                    <span>{formatINR(Number(proforma.sgstTotal ?? 0))}</span>
                  </div>
                </>
              )}
              {proforma.gstApplicable && !intraState && (
                <div className="flex justify-between text-muted-foreground">
                  <span>IGST</span>
                  <span>{formatINR(Number(proforma.igstTotal ?? 0))}</span>
                </div>
              )}
              {Number(proforma.securityDepositTotal ?? 0) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Security deposit (refundable)</span>
                  <span>{formatINR(Number(proforma.securityDepositTotal))}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1 text-base font-semibold">
                <span>Grand Total</span>
                <span>{formatINR(Number(proforma.grandTotal))}</span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// One Pay-Per-Use submission can spawn N stock_out rows sharing a
// cart_id — the Transactions register merges them into one display
// row. The shape below is also passed to MarkStockOutPaidDialog /
// ViewStockOutReceiptDialog so a single dialog handles both single-
// row legacy stock-outs and multi-line carts.
export type CartGroup = {
  id: string;              // synthetic React key — cartId or "singleton-<rowId>"
  cartId: string | null;   // actual stock_out.cart_id; null = singleton
  rows: StockOut[];
  totalAmount: number;
  totalQty: number;
  isPaid: boolean;
  earliestDate: string;
};

function TransactionsList({
  accountId,
}: {
  accountId: number;
}) {
  const { data, isLoading } = useListMemberCharges({ memberId: accountId, pageSize: 200 });
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateCharge = useUpdateMemberCharge();
  const genChargeReceipt = useGenerateMemberChargeReceipt();
  const [generatingChargeId, setGeneratingChargeId] = useState<number | null>(null);
  const [viewingCharge, setViewingCharge] = useState<MemberCharge | null>(null);

  // Phase 31 — Receipt | Invoice tab state for the charge View modal.
  // chargeTab: which tab is active. chargeInvoiceLoaded: whether invoice has been
  // fetched at least once (for this modal open). chargeInvoiceNumber/chargeInvoicePdfUrl:
  // the tax invoice document state, seeded from viewingCharge on open.
  const [chargeTab, setChargeTab] = useState<"receipt" | "invoice">("receipt");
  const [chargeInvoiceLoaded, setChargeInvoiceLoaded] = useState(false);
  const [chargeInvoiceNumber, setChargeInvoiceNumber] = useState<string>("");
  const [chargeInvoicePdfUrl, setChargeInvoicePdfUrl] = useState<string | null>(null);
  const [chargeBusy, setChargeBusy] = useState<null | "invoice" | "whatsapp" | "email">(null);

  // Seed invoice state from the charge when the modal opens.
  // Pre-populate the cached number + PDF so the Invoice tab shows instantly, but
  // always leave it "unloaded" so opening the Invoice tab re-generates the PDF
  // against the CURRENT invoice template. The invoice NUMBER is preserved (the
  // server reuses charge.invoiceNumber idempotently) — only the rendered PDF
  // refreshes, so template edits (e.g. commercial terms) take effect without the
  // operator having to burn a new number.
  useEffect(() => {
    if (!viewingCharge) return;
    setChargeTab("receipt");
    setChargeInvoiceNumber(viewingCharge.invoiceNumber ?? "");
    setChargeInvoicePdfUrl(viewingCharge.invoicePdfUrl ?? null);
    setChargeInvoiceLoaded(false);
    setChargeBusy(null);
  }, [viewingCharge?.id]);

  // Phase 34 — "Record Payment" / "Edit Payment" dialog state.
  // Opens ChargeRecordPaymentDialog for a charge row; null = closed.
  const [recordPaymentCharge, setRecordPaymentCharge] = useState<MemberCharge | null>(null);

  // Invalidate the charges list after a receipt action.
  function invalidateCharges() {
    queryClient.invalidateQueries({
      queryKey: getListMemberChargesQueryKey({ memberId: accountId, pageSize: 200 }),
    });
  }

  // Issue (generate) the receipt for a charge without delivering it.
  async function onGenerateCharge(c: MemberCharge) {
    setGeneratingChargeId(c.id);
    try {
      const r = await genChargeReceipt.mutateAsync({ id: c.id, data: {} });
      invalidateCharges();
      const display = r.receiptNumber ? formatOrderReceiptDisplay(r.receiptNumber, new Date(c.transactionDate)) : "";
      toast({ title: display ? `Receipt #${display} generated.` : "Receipt generated." });
    } catch {
      toast({ title: "Couldn't generate the receipt.", variant: "destructive" });
    } finally {
      setGeneratingChargeId(null);
    }
  }

  // Generate the receipt and deliver via the chosen channel.
  async function onChargeReceipt(c: MemberCharge, channel: "whatsapp" | "email") {
    try {
      const r = await genChargeReceipt.mutateAsync({ id: c.id, data: { channel } });
      invalidateCharges();
      toast({
        title:
          channel === "whatsapp"
            ? r.sent ? "Shared on WhatsApp." : "WhatsApp not configured or no number on file."
            : r.sent ? "Receipt emailed." : "Email not configured or no address on file.",
      });
    } catch {
      toast({ title: "Couldn't generate the receipt.", variant: "destructive" });
    }
  }

  // Phase 31: Tax-invoice helpers for the charge View modal.
  // Lazy-generate the tax invoice the first time the Invoice tab opens.
  async function loadChargeInvoice(charge: MemberCharge) {
    if (chargeInvoiceLoaded) return;
    setChargeBusy("invoice");
    try {
      // Wave 1 contract: documentType:'invoice' body triggers the invoice branch.
      const body: GenerateMemberChargeReceiptBody = { documentType: "invoice" };
      const r = await genChargeReceipt.mutateAsync({ id: charge.id, data: body });
      // Wave 1 response: invoice number rides on the receiptNumber key.
      setChargeInvoiceNumber(r.receiptNumber ?? "");
      setChargeInvoicePdfUrl(r.pdfUrl ?? null);
      setChargeInvoiceLoaded(true);
      invalidateCharges();
    } catch {
      toast({ title: "Couldn't generate the invoice.", variant: "destructive" });
    } finally {
      setChargeBusy(null);
    }
  }

  // Operator renumber of an issued charge tax invoice. `number` sets it exactly;
  // `auto` re-mints under the current rule (day pass -> plan series). The server
  // refuses a number already used in this space (409) and audit-logs the change.
  const [chargeInvoiceEdit, setChargeInvoiceEdit] = useState<string>("");
  async function renumberChargeInvoice(charge: MemberCharge, mode: "number" | "auto") {
    const typed = chargeInvoiceEdit.trim().replace(/^#/, "");
    if (mode === "number" && !typed) return;
    setChargeBusy("invoice");
    try {
      const body: GenerateMemberChargeReceiptBody & { invoiceNumber?: string; renumber?: boolean } =
        mode === "number"
          ? { documentType: "invoice", invoiceNumber: typed }
          : { documentType: "invoice", renumber: true };
      const r = await genChargeReceipt.mutateAsync({ id: charge.id, data: body });
      setChargeInvoiceNumber(r.receiptNumber ?? "");
      setChargeInvoiceEdit("");
      setChargeInvoicePdfUrl(r.pdfUrl ?? null);
      setChargeInvoiceLoaded(true);
      invalidateCharges();
      toast({ title: `Invoice number updated to #${r.receiptNumber ?? ""}.` });
    } catch (err) {
      const code = (err as { data?: { code?: string } } | null)?.data?.code;
      toast({
        title:
          code === "invoice_number_taken"
            ? "That invoice number is already used."
            : code === "invalid_invoice_number"
              ? "Use the format NN/MMYYYY (e.g. 03/112026)."
              : "Couldn't update the invoice number.",
        variant: "destructive",
      });
    } finally {
      setChargeBusy(null);
    }
  }

  // Share the active charge document (receipt or invoice) via a channel.
  async function shareChargeDoc(charge: MemberCharge, channel: "whatsapp" | "email") {
    const sharingInvoice = chargeTab === "invoice";
    setChargeBusy(channel);
    try {
      const body: GenerateMemberChargeReceiptBody = sharingInvoice
        ? { documentType: "invoice", channel }
        : { channel };
      const r = await genChargeReceipt.mutateAsync({ id: charge.id, data: body });
      if (r.pdfUrl) {
        if (sharingInvoice) setChargeInvoicePdfUrl(r.pdfUrl);
      }
      invalidateCharges();
      const docLabel = sharingInvoice ? "Invoice" : "Receipt";
      toast({
        title:
          channel === "whatsapp"
            ? r.sent ? "Shared on WhatsApp." : "WhatsApp not configured or no number on file."
            : r.sent ? `${docLabel} emailed.` : "Email not configured or no address on file.",
      });
    } catch {
      toast({ title: "Couldn't share the document.", variant: "destructive" });
    } finally {
      setChargeBusy(null);
    }
  }

  // Pay-Per-Use stock-outs (F&B, decor, add-ons) booked against this account.
  // We surface them under F&B (and Add-ons by item category) so the operator
  // can see and reconcile them from the same page as plan/booking charges.
  const { data: stockOutData, isLoading: stockOutLoading } = useListStockOut({
    memberId: accountId,
    consumptionType: "Member_Paid_Use",
    pageSize: 200,
  });
  // Item lookup powers the description ("Lays Chips 50g") and category routing
  // (which sidebar bucket the row lands in).
  const { data: items = [] } = useListInventoryItems({ status: "Active" });
  const { data: categories = [] } = useListInventoryCategories();
  // Contacts for this account drive the consumer name on F&B stock-out
  // rows — each row's contactId resolves to a fullName here.
  const { data: accountContacts = [] } = useListAccountContacts(accountId);
  const contactNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of accountContacts) m.set(c.id, c.fullName);
    return m;
  }, [accountContacts]);
  // Pay-Per-Use Mark-as-Paid is now a dialog that captures Payment
  // Mode / Transaction ID / Merchant Copy. The dialog handles the
  // PATCH + query invalidation; here we just track which cart is open.
  const [markPaidTarget, setMarkPaidTarget] = useState<CartGroup | null>(null);
  // Read-only "View Receipt" dialog target — shows the captured
  // transaction id + proof file for an already-Paid cart.
  const [viewReceiptTarget, setViewReceiptTarget] = useState<CartGroup | null>(null);
  const [selected, setSelected] = useState<Category>("Plan");

  // Delete a PENDING Pay-Per-Use stock-out entry (per stock_out id). Backend
  // reverses the inventory + entitlement effects atomically and refuses (409
  // stock_out_not_pending) anything paid / receipted. We call the endpoint
  // directly (no orval hook — codegen-collision avoidance) and invalidate the
  // stock-out list so the row disappears. A confirm AlertDialog gates the click.
  const stockOutListKey = getListStockOutQueryKey({
    memberId: accountId,
    consumptionType: "Member_Paid_Use",
    pageSize: 200,
  });
  const [deleteTarget, setDeleteTarget] = useState<StockOut | null>(null);
  const deleteStockOut = useMutation({
    mutationFn: (id: number) => deleteStockOutDirect(id),
    onSuccess: () => {
      setDeleteTarget(null);
      toast({ title: "Entry deleted." });
      queryClient.invalidateQueries({ queryKey: stockOutListKey });
      // Pay-Per-Use entries surface alongside member-charges and the plan
      // schedule; refresh those too so any derived totals stay in sync.
      queryClient.invalidateQueries({
        queryKey: getListMemberChargesQueryKey({ memberId: accountId, pageSize: 200 }),
      });
      queryClient.invalidateQueries({
        queryKey: getGetAccountPlanScheduleQueryKey(accountId),
      });
    },
    onError: (err) => {
      const code = err instanceof StockOutDeleteError ? err.code : null;
      toast({
        title:
          code === "stock_out_not_pending"
            ? "Only pending entries can be deleted."
            : "Couldn't delete the entry.",
        variant: "destructive",
      });
    },
  });
  // A stock_out row is Pending-deletable when it isn't Paid and has no receipt
  // linkage (no invoice reference, no member_charge_id). Mirrors the backend gate.
  function isStockOutDeletable(r: StockOut): boolean {
    const ps = (r.paymentStatus ?? "").trim().toLowerCase();
    const invRef = (r as unknown as { invoiceReference?: string | null }).invoiceReference;
    const chargeId = (r as unknown as { memberChargeId?: number | null }).memberChargeId;
    return (
      ps !== "paid" &&
      !(typeof invRef === "string" && invRef.trim().length > 0) &&
      chargeId == null
    );
  }

  // Look up tables for stock-out → category routing.
  const itemById = useMemo(() => {
    const m = new Map<number, InventoryItem>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);
  const categoryNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  // Match the item's category name to a Transactions sidebar bucket so a
  // Pay-Per-Use stock-out lands in the same place an operator would expect
  // to look for it ("F&B" stock-out → F&B section). The match is name-
  // based (case-insensitive, substring) so categories like "Food & Bev"
  // or "Beverages" still route to F&B without code changes.
  function categoryForItem(itemId: number): Category {
    const cat = itemById.get(itemId);
    const name = cat ? categoryNameById.get(cat.categoryId) ?? "" : "";
    const lower = name.toLowerCase();
    if (/(f\s*&\s*b|food|bever|drink|snack|cafe|coffee)/.test(lower)) return "F&B";
    if (/(print|paper|photo)/.test(lower)) return "Prints";
    if (/(station|pen|notebook)/.test(lower)) return "Stationary";
    if (/(add[-\s]?on|optional|accessory)/.test(lower)) return "Add-ons";
    if (/(conference|meeting)/.test(lower)) return "Conference Room";
    return "F&B";
  }

  const grouped = useMemo<Record<Category, MemberCharge[]>>(() => {
    const out: Record<Category, MemberCharge[]> = {
      Plan: [],
      Booking: [],
      "Conference Room": [],
      "Add-ons": [],
      "F&B": [],
      Prints: [],
      Stationary: [],
      Miscellaneous: [],
    };
    // useListMemberCharges returns the MemberChargeListResponse envelope
    // {items, total, page, pageSize} — read .items, not the raw .data.
    const chargeItems: MemberCharge[] = data?.items ?? [];
    for (const c of chargeItems) {
      out[categoryForSourceType(c.sourceType)].push(c);
    }
    return out;
  }, [data]);

  // Pay-Per-Use stock-outs grouped by item-category. These are not
  // member_charges rows yet — they live in stock_out and surface here
  // so the operator can see and "Mark as Paid" them next to plan/
  // booking charges. Falls back to F&B when category routing is
  // ambiguous so nothing silently disappears.
  const stockOutByCategory = useMemo<Record<Category, StockOut[]>>(() => {
    const out: Record<Category, StockOut[]> = {
      Plan: [],
      Booking: [],
      "Conference Room": [],
      "Add-ons": [],
      "F&B": [],
      Prints: [],
      Stationary: [],
      Miscellaneous: [],
    };
    const rows: StockOut[] = stockOutData?.items ?? [];
    for (const r of rows) {
      out[categoryForItem(r.itemId)].push(r);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockOutData, itemById, categoryNameById]);

  // Multi-item Pay-Per-Use submissions share a cart_id; the
  // Transactions register surfaces them as ONE row (with the total
  // and an expand to see per-item breakdown) so the operator's view
  // matches what was actually paid (one UPI transfer = one cart).
  // Rows with NULL cart_id (legacy / single-item submissions) render
  // as their own singleton carts.
  function buildCart(rows: StockOut[], cartId: string | null): CartGroup {
    const totalAmount = rows.reduce(
      (s, r) => s + Number(r.amountCharged ?? 0),
      0,
    );
    const totalQty = rows.reduce(
      (s, r) => s + Number(r.quantityIssued),
      0,
    );
    const isPaid = rows.every((r) => r.paymentStatus === "Paid");
    const earliestDate = rows.reduce(
      (min, r) => (r.transactionDate < min ? r.transactionDate : min),
      rows[0]!.transactionDate,
    );
    return {
      id: cartId ?? `singleton-${rows[0]!.id}`,
      cartId,
      rows,
      totalAmount,
      totalQty,
      isPaid,
      earliestDate,
    };
  }
  const cartGroupsByCategory = useMemo<Record<Category, CartGroup[]>>(() => {
    const out = {} as Record<Category, CartGroup[]>;
    for (const cat of CATEGORY_LABELS) {
      const rows = stockOutByCategory[cat];
      const byCart = new Map<string, StockOut[]>();
      const singletons: StockOut[] = [];
      for (const r of rows) {
        const cid = (r as unknown as { cartId?: string | null }).cartId ?? null;
        if (cid) {
          const arr = byCart.get(cid) ?? [];
          arr.push(r);
          byCart.set(cid, arr);
        } else {
          singletons.push(r);
        }
      }
      const groups: CartGroup[] = [];
      for (const [cid, rs] of byCart.entries()) groups.push(buildCart(rs, cid));
      for (const r of singletons) groups.push(buildCart([r], null));
      groups.sort((a, b) => b.earliestDate.localeCompare(a.earliestDate));
      out[cat] = groups;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockOutByCategory]);

  // Per-cart expand toggle for the merged row.
  const [expandedCarts, setExpandedCarts] = useState<Set<string>>(new Set());
  function toggleCartExpand(id: string) {
    setExpandedCarts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // 2026-07-15 — retro-bundle collect. Operator ticks 2+ Pending Pay-Per-Use
  // carts sold at different times, one action bar shows the sum + Collect
  // button that opens the mark-paid Sheet with all selected rows folded into
  // a synthetic multi-row CartTarget (settled via POST /bulk-payment).
  const [selectedCartIds, setSelectedCartIds] = useState<Set<string>>(new Set());
  function toggleCartSelect(id: string) {
    setSelectedCartIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const totalByCategory = useMemo<Record<Category, number>>(() => {
    const out = {} as Record<Category, number>;
    for (const cat of CATEGORY_LABELS) {
      const chargesSum = grouped[cat].reduce((sum, c) => sum + Number(c.total ?? 0), 0);
      const stockSum = stockOutByCategory[cat].reduce(
        (sum, r) => sum + Number(r.amountCharged ?? 0),
        0,
      );
      out[cat] = chargesSum + stockSum;
    }
    return out;
  }, [grouped, stockOutByCategory]);

  if (isLoading || stockOutLoading) {
    return (
      <div className="space-y-2 pt-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded" />
        ))}
      </div>
    );
  }

  const rows = grouped[selected];
  const stockCarts = cartGroupsByCategory[selected];
  const totalRowCount = rows.length + stockCarts.length;

  return (
    // Stock-In layout: icon sidebar (aside) + content section (Card) on the right.
    <div className="flex flex-col gap-8 pt-2 lg:flex-row">
      {/* Sidebar — category icon rail (no Order Receipt; that lives in the header). */}
      <aside className="shrink-0 space-y-2 lg:w-[220px]">
        {CATEGORY_LABELS.map((cat) => {
          const active = selected === cat;
          const Icon = CATEGORY_ICONS[cat];
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setSelected(cat)}
              aria-current={active ? "page" : undefined}
              className={`flex h-11 w-full items-center gap-3 rounded-md px-3 text-left transition-colors ${
                active ? "bg-primary text-primary-foreground" : "hover:bg-muted/60"
              }`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="truncate text-sm font-medium">{cat}</span>
            </button>
          );
        })}
      </aside>

      {/* Content — the selected category's transactions, in its own section.
          "Plan" shows the recurring payment schedule (one row per billing
          period); every other category shows its member-charge transactions. */}
      <main className="min-w-0 flex-1">
        {selected === "Plan" ? (
          <PlanSchedule accountId={accountId} />
        ) : (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-base">{selected}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {totalRowCount} {totalRowCount === 1 ? "transaction" : "transactions"}
              </p>
            </div>
            <span className="text-sm font-semibold tabular-nums">
              {formatINR(totalByCategory[selected])}
            </span>
          </CardHeader>
          <CardContent>
            {totalRowCount === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No {selected.toLowerCase()} transactions yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-32" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={`charge-${c.id}`}>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(c.transactionDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-sm">{c.description}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="text-[10px]">
                          {c.sourceType.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs">{Number(c.quantity)}</TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatINR(Number(c.total))}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={c.status === "Paid" ? "default" : "secondary"}
                          className="capitalize text-[10px]"
                        >
                          {c.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <TooltipProvider delayDuration={200}>
                          {c.receiptNumber ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="font-mono text-xs font-medium tabular-nums">
                                #{formatOrderReceiptDisplay(c.receiptNumber, new Date(c.transactionDate))}
                              </span>
                              <div className="flex items-center gap-0.5">
                                <PlanScheduleIconButton
                                  label="View Receipt"
                                  icon={Eye}
                                  onClick={() => setViewingCharge(c)}
                                />
                                <PlanScheduleIconButton
                                  label="Share on WhatsApp"
                                  icon={MessageCircle}
                                  onClick={() => onChargeReceipt(c, "whatsapp")}
                                />
                                <PlanScheduleIconButton
                                  label="Share on Email"
                                  icon={Mail}
                                  onClick={() => onChargeReceipt(c, "email")}
                                />
                                {c.status !== "Waived" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={() => setRecordPaymentCharge(c)}
                                  >
                                    {c.status === "Paid" ? "Edit Payment" : "Record Payment"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1.5 text-xs"
                                disabled={generatingChargeId === c.id}
                                onClick={() => onGenerateCharge(c)}
                              >
                                {generatingChargeId === c.id
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Receipt className="h-3.5 w-3.5" />}
                                Generate Receipt
                              </Button>
                              {c.status !== "Waived" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => setRecordPaymentCharge(c)}
                                >
                                  {c.status === "Paid" ? "Edit Payment" : "Record Payment"}
                                </Button>
                              )}
                            </div>
                          )}
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                  {stockCarts.map((cart) => {
                    const first = cart.rows[0]!;
                    const isMulti = cart.rows.length > 1;
                    const isExpanded = expandedCarts.has(cart.id);
                    const isSelected = selectedCartIds.has(cart.id);
                    // contactId / consumer name come off the first row;
                    // all rows in a cart share the same consumer.
                    const contactId =
                      (first as unknown as { contactId?: number | null }).contactId ?? null;
                    const consumerName =
                      contactId != null ? contactNameById.get(contactId) : null;
                    const headDescription = isMulti
                      ? `Cart of ${cart.rows.length} items`
                      : itemById.get(first.itemId)?.name ?? `Item #${first.itemId}`;
                    return (
                      <Fragment key={`cart-${cart.id}`}>
                        <TableRow className={isSelected ? "bg-primary/5" : undefined}>
                          <TableCell className="text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              {/* 2026-07-15 — retro-bundle checkbox. Only
                                  offered when the cart is still Pending, so
                                  Paid carts can't be re-selected. */}
                              {!cart.isPaid ? (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleCartSelect(cart.id)}
                                  className="mr-1 h-3.5 w-3.5 cursor-pointer accent-primary"
                                  aria-label={isSelected ? "Unselect cart for bulk pay" : "Select cart for bulk pay"}
                                />
                              ) : null}
                              {isMulti ? (
                                <button
                                  type="button"
                                  onClick={() => toggleCartExpand(cart.id)}
                                  className="-ml-1 p-1 rounded hover:bg-muted"
                                  aria-label={isExpanded ? "Collapse cart" : "Expand cart"}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  )}
                                </button>
                              ) : null}
                              <span>{format(new Date(cart.earliestDate), "dd MMM yyyy")}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex flex-col">
                              <span>{headDescription}</span>
                              {consumerName && (
                                <span className="text-[10px] text-muted-foreground">
                                  Consumed by: {consumerName}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-[10px]">
                              Stock-Out
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-xs">{cart.totalQty}</TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {formatINR(cart.totalAmount)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={cart.isPaid ? "default" : "secondary"}
                              className="capitalize text-[10px]"
                            >
                              {cart.isPaid ? "Paid" : "Pending"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {cart.isPaid ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => setViewReceiptTarget(cart)}
                              >
                                View
                              </Button>
                            ) : isSelected ? (
                              // Hide per-cart CTA once ticked — the floating
                              // action bar's Collect payment is the single
                              // action path while a bulk selection is active.
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => setMarkPaidTarget(cart)}
                                >
                                  Mark as Paid
                                </Button>
                                {/* Single-row pending cart → delete the one
                                    stock_out here. Multi-row carts get a
                                    per-row delete in the expanded section
                                    below (each pending row, by stock_out id). */}
                                {!isMulti && isStockOutDeletable(first) && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    aria-label="Delete entry"
                                    title="Delete entry"
                                    disabled={
                                      deleteStockOut.isPending &&
                                      deleteTarget?.id === first.id
                                    }
                                    onClick={() => setDeleteTarget(first)}
                                  >
                                    {deleteStockOut.isPending &&
                                    deleteTarget?.id === first.id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                        {isMulti && isExpanded
                          ? cart.rows.map((r) => {
                              const it = itemById.get(r.itemId);
                              return (
                                <TableRow
                                  key={`cart-${cart.id}-item-${r.id}`}
                                  className="bg-muted/30"
                                >
                                  <TableCell />
                                  <TableCell className="text-xs pl-10">
                                    · {it?.name ?? `Item #${r.itemId}`}
                                  </TableCell>
                                  <TableCell />
                                  <TableCell className="text-right text-xs">
                                    {Number(r.quantityIssued)}
                                  </TableCell>
                                  <TableCell className="text-right text-xs">
                                    {formatINR(Number(r.amountCharged ?? 0))}
                                  </TableCell>
                                  <TableCell />
                                  <TableCell className="text-right">
                                    {/* Per-row delete for a pending cart line
                                        (each its own stock_out id). */}
                                    {isStockOutDeletable(r) && (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                        aria-label="Delete entry"
                                        title="Delete entry"
                                        disabled={
                                          deleteStockOut.isPending &&
                                          deleteTarget?.id === r.id
                                        }
                                        onClick={() => setDeleteTarget(r)}
                                      >
                                        {deleteStockOut.isPending &&
                                        deleteTarget?.id === r.id ? (
                                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                          <Trash2 className="h-3.5 w-3.5" />
                                        )}
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          : null}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {/* 2026-07-15 — retro-bundle action bar. Appears when 2+ Pending
                Pay-Per-Use carts are ticked, showing the running sum and a
                Collect button that opens the mark-paid Sheet with all
                selected rows folded into a synthetic multi-row target. */}
            {(() => {
              if (selectedCartIds.size === 0) return null;
              const chosen = stockCarts.filter((c) => selectedCartIds.has(c.id));
              if (chosen.length < 2) return null;
              const flatRows = chosen.flatMap((c) => c.rows);
              const total = chosen.reduce((s, c) => s + c.totalAmount, 0);
              const synth: CartGroup = {
                id: `bulk-${chosen.map((c) => c.id).join("-")}`,
                cartId: null,
                rows: flatRows,
                totalAmount: total,
                totalQty: flatRows.reduce((s, r) => s + Number(r.quantityIssued), 0),
                isPaid: false,
                earliestDate: chosen
                  .map((c) => c.earliestDate)
                  .sort()[0] ?? chosen[0]!.earliestDate,
              };
              return (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                  <div className="text-sm">
                    <span className="font-medium">{selectedCartIds.size}</span>{" "}
                    <span className="text-muted-foreground">
                      pending {selectedCartIds.size === 1 ? "entry" : "entries"} selected · total{" "}
                    </span>
                    <span className="font-mono font-semibold tabular-nums">
                      {formatINR(total)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setSelectedCartIds(new Set())}
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setMarkPaidTarget(synth)}
                    >
                      Collect payment {formatINR(total)}
                    </Button>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
        )}
      </main>

      {/* Phase 34 — Record / Edit Payment dialog for charge rows. */}
      {recordPaymentCharge && (
        <ChargeRecordPaymentDialog
          charge={recordPaymentCharge}
          onClose={() => {
            setRecordPaymentCharge(null);
            invalidateCharges();
          }}
        />
      )}

      <MarkStockOutPaidDialog
        cart={markPaidTarget}
        open={markPaidTarget != null}
        onOpenChange={(o) => {
          if (!o) {
            setMarkPaidTarget(null);
            // 2026-07-15 — also clear the retro-bundle selection so the
            // action bar collapses after a successful (or cancelled) bulk
            // pay. Harmless when the dialog was opened from a single cart.
            setSelectedCartIds(new Set());
          }
        }}
        itemById={itemById}
      />
      <ViewStockOutReceiptDialog
        cart={viewReceiptTarget}
        open={viewReceiptTarget != null}
        onOpenChange={(o) => { if (!o) setViewReceiptTarget(null); }}
        itemById={itemById}
      />

      {/* Confirm dialog for deleting a PENDING Pay-Per-Use stock-out entry. */}
      <AlertDialog
        open={deleteTarget != null}
        onOpenChange={(o) => { if (!o && !deleteStockOut.isPending) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the Pending charge and restores any stock it consumed.
              This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteStockOut.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteStockOut.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteStockOut.mutate(deleteTarget.id);
              }}
            >
              {deleteStockOut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 31 — Charge View modal: Receipt | Invoice tabs.
          Receipt tab = Phase-30 order receipt (unchanged behavior).
          Invoice tab = GST tax invoice, disabled until charge is fully Paid.
          Mirrors the Plan ReceiptModal structure. */}
      <Dialog
        open={viewingCharge != null}
        onOpenChange={(o) => {
          if (!o) {
            setViewingCharge(null);
            // Reset invoice tab state so next open is fresh.
            setChargeTab("receipt");
            setChargeInvoiceLoaded(false);
            setChargeInvoiceNumber("");
            setChargeInvoiceEdit("");
            setChargeInvoicePdfUrl(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <DialogTitle>
                  {chargeTab === "invoice" ? "Tax Invoice" : "Order Receipt"} —{" "}
                  {(viewingCharge?.description ?? "Charge").replace(/_/g, " ")}
                </DialogTitle>
                <DialogDescription>
                  {chargeTab === "invoice"
                    ? (viewingCharge?.invoiceNumber ?? chargeInvoiceNumber
                        ? `#${chargeInvoiceNumber || viewingCharge?.invoiceNumber}`
                        : "")
                    : (viewingCharge?.receiptNumber
                        ? `#${formatOrderReceiptDisplay(viewingCharge.receiptNumber, new Date(viewingCharge.transactionDate))}`
                        : "")}
                </DialogDescription>
              </div>
              {/* Receipt vs Invoice tab toggle. Invoice disabled until status=Paid. */}
              <Tabs
                value={chargeTab}
                onValueChange={(v) => {
                  const next = v === "invoice" ? "invoice" : "receipt";
                  setChargeTab(next);
                  if (next === "invoice" && viewingCharge) void loadChargeInvoice(viewingCharge);
                }}
              >
                <TabsList>
                  <TabsTrigger value="receipt">Receipt</TabsTrigger>
                  <TabsTrigger
                    value="invoice"
                    disabled={viewingCharge?.status !== "Paid" || !Number(viewingCharge?.gstRate ?? 0)}
                    title={
                      viewingCharge?.status !== "Paid"
                        ? "Available once paid"
                        : !Number(viewingCharge?.gstRate ?? 0)
                          ? "No GST applies — a tax invoice requires GST; use the receipt"
                          : undefined
                    }
                  >
                    Tax Invoice
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </DialogHeader>

          {/* Invoice tab: tax invoice number (read-only, plain markup — no FormField needed). */}
          {chargeTab === "invoice" && (
            <div className="space-y-1">
              <Label className="text-xs">Tax invoice no.</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={
                    chargeInvoiceEdit !== ""
                      ? chargeInvoiceEdit
                      : chargeInvoiceNumber || viewingCharge?.invoiceNumber
                        ? `#${chargeInvoiceNumber || viewingCharge?.invoiceNumber}`
                        : ""
                  }
                  onChange={(e) => setChargeInvoiceEdit(e.target.value)}
                  placeholder="Auto (e.g. #01/062026)"
                  className="h-9 font-mono"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9"
                  disabled={chargeBusy !== null || chargeInvoiceEdit.trim() === ""}
                  onClick={() => viewingCharge && void renumberChargeInvoice(viewingCharge, "number")}
                >
                  Update
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9"
                  disabled={chargeBusy !== null}
                  title="Re-issue under the current numbering rule (day passes use the plan series)"
                  onClick={() => viewingCharge && void renumberChargeInvoice(viewingCharge, "auto")}
                >
                  Auto
                </Button>
              </div>
            </div>
          )}

          {/* PDF preview area — receipt or invoice PDF depending on active tab. */}
          <div className="h-[62vh] overflow-hidden rounded-md border bg-muted/10">
            {chargeTab === "invoice" ? (
              chargeInvoicePdfUrl || viewingCharge?.invoicePdfUrl ? (
                <iframe
                  key={chargeInvoicePdfUrl ?? viewingCharge?.invoicePdfUrl ?? ""}
                  src={`${chargeInvoicePdfUrl ?? viewingCharge?.invoicePdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                  className="h-full w-full"
                  title="Tax invoice"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {chargeBusy === "invoice" ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Generating invoice…
                    </span>
                  ) : (
                    "Switch to the Invoice tab to generate the tax invoice."
                  )}
                </div>
              )
            ) : (
              viewingCharge?.receiptPdfUrl ? (
                <iframe
                  key={viewingCharge.receiptPdfUrl}
                  src={`${viewingCharge.receiptPdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                  className="h-full w-full"
                  title="Order receipt"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  No PDF yet — try generating the receipt again.
                </div>
              )
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2 sm:justify-start">
            {chargeTab === "invoice" ? (
              // Invoice tab actions — Download/WhatsApp/Email for the tax invoice.
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!(chargeInvoicePdfUrl ?? viewingCharge?.invoicePdfUrl)}
                  onClick={() => {
                    const url = chargeInvoicePdfUrl ?? viewingCharge?.invoicePdfUrl;
                    if (url) window.open(url, "_blank", "noopener");
                  }}
                >
                  <Download className="mr-1.5 h-4 w-4" /> Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!!chargeBusy}
                  onClick={() => viewingCharge && shareChargeDoc(viewingCharge, "whatsapp")}
                >
                  <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!!chargeBusy}
                  onClick={() => viewingCharge && shareChargeDoc(viewingCharge, "email")}
                >
                  <Mail className="mr-1.5 h-4 w-4" /> Email
                </Button>
              </>
            ) : (
              // Receipt tab actions — unchanged Phase-30 behavior.
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!viewingCharge?.receiptPdfUrl}
                  onClick={() => viewingCharge?.receiptPdfUrl && window.open(viewingCharge.receiptPdfUrl, "_blank", "noopener")}
                >
                  <Download className="mr-1.5 h-4 w-4" /> Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={genChargeReceipt.isPending}
                  onClick={() => viewingCharge && onChargeReceipt(viewingCharge, "whatsapp")}
                >
                  <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={genChargeReceipt.isPending}
                  onClick={() => viewingCharge && onChargeReceipt(viewingCharge, "email")}
                >
                  <Mail className="mr-1.5 h-4 w-4" /> Email
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="sm:ml-auto"
              onClick={() => {
                setViewingCharge(null);
                setChargeTab("receipt");
                setChargeInvoiceLoaded(false);
                setChargeInvoiceNumber("");
            setChargeInvoiceEdit("");
                setChargeInvoicePdfUrl(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Plan payment schedule ──────────────────────────────────────────────────
// One row per billing period for each recurring plan (membership), with paid
// status, an editable reminder date, and per-period order-receipt actions.
function PlanScheduleTile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-base font-semibold tabular-nums">{value}</p>
      <p className="text-[11px] text-muted-foreground">{sub}</p>
    </div>
  );
}

// Display-only order-receipt id. The DB stores a plain continuous serial (e.g.
// "9"); we render it as "#09/MMYYYY" where MM/YYYY is the period's START month
// and year — so #09 is the 9th order receipt issued and /042026 marks an Apr
// 2026 period. A non-numeric stored value (a legacy "NN/MMYYYY" receipt or an
// operator override) is shown as-is. Keeping the suffix display-only leaves the
// continuous numbering counter (next = highest plain int + 1) intact.
function formatOrderReceiptId(receiptNumber: string, periodStart: string): string {
  const serial = receiptNumber.trim();
  if (!/^\d+$/.test(serial)) return serial.startsWith("#") ? serial : `#${serial}`;
  const [yyyy, mm] = periodStart.split("-");
  return `#${serial.padStart(2, "0")}/${mm}${yyyy}`;
}

// Grouped plan periods that share the same period start date across all of an
// account's memberships — used by the merged "Combined" row in the schedule
// table and by the BundleReceiptModal.
type DealPeriodGroup = {
  id: string;
  quoteGroupId: string | null;
  periodStart: string;
  members: PlanSchedulePeriod[];
  totalAmount: number;
  totalAmountPaid: number;
};

function PlanSchedule({ accountId }: { accountId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetAccountPlanSchedule(accountId);
  // Per-membership held deposit → so Record Payment on the FIRST period can show
  // the receipt's full "rent + one-time deposit" total (the deposit is billed on
  // period 0 only; see the receipt's grandTotal in plan-schedule.ts).
  const { data: scheduleMemberships } = useListAccountMemberships(accountId);
  const depositByMembership = useMemo(() => {
    const m = new Map<number, number>();
    for (const ms of (scheduleMemberships ?? []) as Membership[]) {
      if (ms.securityDepositHeld != null) m.set(ms.id, Number(ms.securityDepositHeld));
    }
    return m;
  }, [scheduleMemberships]);
  // Quick task 9 — Held deposits eligible to be applied toward rent due on
  // the Record Payment forms (default query = Held only, matches the
  // Restructure picker's convention).
  const { data: heldDepositsData } = useGetAccountDeposits(accountId);
  const heldDeposits = heldDepositsData?.items ?? [];
  // Quick task 9 — Issued/Adjusted credit notes eligible to be applied
  // toward rent due (NOT Refunded/Applied — those are terminal/consumed).
  const { data: accountCreditNotes } = useListAccountCreditNotes(accountId, {
    query: { queryKey: getListAccountCreditNotesQueryKey(accountId) },
  });
  const applicableCreditNotes = (accountCreditNotes ?? []).filter(
    (cn) => cn.settlementStatus === "Issued" || cn.settlementStatus === "Adjusted",
  );
  // Committed term (durationMonths) per membership, so period rows show a
  // uniform "N mo" subtext matching the Plan tab — not the cycle label
  // (Monthly / One Time). PlanSchedulePeriod carries no durationMonths, so we
  // look it up from the membership by id.
  const durationByMembership = useMemo(() => {
    const m = new Map<number, number>();
    for (const ms of (scheduleMemberships ?? []) as Membership[]) {
      if (ms.durationMonths != null) m.set(ms.id, Number(ms.durationMonths));
    }
    return m;
  }, [scheduleMemberships]);
  const updateReminder = useUpdatePlanPeriodReminder();
  const genReceipt = useGeneratePlanPeriodReceipt();
  // Bundle-receipt re-share — when the merged Plan-deal row's WhatsApp /
  // Email icons are clicked we call the existing bundle endpoint with a
  // channel; backend re-uses the same receiptNumber persisted on every
  // sibling period and updates receiptSentAt.
  const genBundleReceipt = useGenerateBundleReceipt();
  // Phase 28 PERIOD28-05: per-period Edit-amount action.
  const updateAmount = useUpdatePeriodAmount();
  const [editingAmount, setEditingAmount] = useState<PlanSchedulePeriod | null>(null);
  const [editAmountValue, setEditAmountValue] = useState<string>("");
  const [viewing, setViewing] = useState<PlanSchedulePeriod | null>(null);
  // Combined-receipt viewer for merged Plan-deal rows — opens the bundle PDF
  // (regenerated fresh so all sibling lines are guaranteed to be present),
  // not the per-period one. Distinct from `viewing` which opens a single
  // period's ReceiptModal.
  const [viewingBundle, setViewingBundle] = useState<DealPeriodGroup | null>(null);
  const [recording, setRecording] = useState<PlanSchedulePeriod | null>(null);
  // Grouped record-payment for a combined deal row — marks EVERY sibling
  // period in the quote-group paid in one action (one lump sum, one mode/date/
  // proof), so a multi-space-type deal is settled as a single transaction to
  // match its single combined receipt. Distinct from `recording` (one period).
  const [recordingGroup, setRecordingGroup] = useState<DealPeriodGroup | null>(null);
  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [generatingDealId, setGeneratingDealId] = useState<string | null>(null);
  // A deal reminder edit fans out one PATCH per sibling period; lock the input
  // while that runs so a second edit can't interleave with the first.
  const [savingReminderDealId, setSavingReminderDealId] = useState<string | null>(null);

  const periods = data?.periods ?? [];
  const payments = data?.payments ?? [];
  const deposits = data?.deposits ?? [];
  const summary = data?.summary;

  // Group the Payment ledger by DEAL, on the same group-plan basis as the Plan
  // tab / Transactions schedule: the two ledger rows a combined deal payment
  // writes (one per space-type membership) collapse into ONE row labelled by the
  // deal ("Plan #{first line id}"), not split into individual per-space-type
  // plan ids. Grouped by quoteGroupId + paidAt (one combined payment event);
  // payments with no quoteGroupId (solo/legacy) stay their own row.
  // `isDeal` = the payment's membership belongs to a multi-space-type deal
  // (its quote group has ≥2 members) → the row is labelled by the DEAL
  // ("Plan #{first line id}"), NEVER by the individual space type — even for a
  // single (non-time-grouped) event like a deposit / credit-note adjustment.
  // `dealId` = the deal's first-line membership id.
  type LedgerGroup = {
    key: string; items: PlanPayment[]; total: number; isGroup: boolean; isDeal: boolean; dealId?: number;
  };
  const paymentGroups = useMemo<LedgerGroup[]>(() => {
    const mToQg = new Map<number, string | null>();
    const qgMembers = new Map<string, number[]>();
    for (const ms of (scheduleMemberships ?? []) as Membership[]) {
      mToQg.set(ms.id, ms.quoteGroupId ?? null);
      if (ms.quoteGroupId) {
        const arr = qgMembers.get(ms.quoteGroupId) ?? [];
        arr.push(ms.id);
        qgMembers.set(ms.quoteGroupId, arr);
      }
    }
    const dealInfo = (mid: number | undefined) => {
      const qg = mid != null ? mToQg.get(mid) ?? null : null;
      const members = qg ? qgMembers.get(qg) ?? [] : [];
      return { isDeal: members.length >= 2, dealId: members.length ? Math.min(...members) : mid };
    };
    const byKey = new Map<string, PlanPayment[]>();
    const order: string[] = [];
    for (const pay of payments) {
      const qg = pay.membershipId != null ? mToQg.get(pay.membershipId) ?? null : null;
      const key = qg ? `deal-${qg}-${pay.paidAt}` : `solo-${pay.id}`;
      if (!byKey.has(key)) { byKey.set(key, []); order.push(key); }
      byKey.get(key)!.push(pay);
    }
    return order.map((key) => {
      const items = byKey.get(key)!;
      const info = dealInfo(items[0]!.membershipId);
      return {
        key,
        items,
        total: Math.round(items.reduce((s, p) => s + Number(p.amount), 0) * 100) / 100,
        isGroup: items.length > 1,
        isDeal: info.isDeal,
        dealId: info.dealId,
      };
    });
  }, [payments, scheduleMemberships]);

  // Group deposits by quoteGroupId so a multi-space-type deal shows ONE
  // "Security deposit collected" row with the summed amount instead of
  // one per membership. Rows with null quoteGroupId (solo / legacy)
  // stay as their own group.
  type DepositGroup = {
    id: string;
    quoteGroupId: string | null;
    collectedDate: string;
    totalAmount: number;
    items: typeof deposits;
  };
  const depositGroups = useMemo<DepositGroup[]>(() => {
    const byGroup = new Map<string, typeof deposits>();
    const solos: typeof deposits = [];
    for (const d of deposits) {
      if (d.quoteGroupId) {
        const arr = byGroup.get(d.quoteGroupId) ?? [];
        arr.push(d);
        byGroup.set(d.quoteGroupId, arr);
      } else {
        solos.push(d);
      }
    }
    const out: DepositGroup[] = [];
    for (const [qg, items] of byGroup.entries()) {
      const earliest = items.reduce(
        (min, d) => (d.collectedDate < min ? d.collectedDate : min),
        items[0]!.collectedDate,
      );
      out.push({
        id: `deal-${qg}`,
        quoteGroupId: qg,
        collectedDate: earliest,
        totalAmount: items.reduce((s, d) => s + Number(d.amount ?? 0), 0),
        items,
      });
    }
    for (const d of solos) {
      out.push({
        id: `solo-${d.membershipId}`,
        quoteGroupId: null,
        collectedDate: d.collectedDate,
        totalAmount: Number(d.amount ?? 0),
        items: [d],
      });
    }
    // Earliest collection date first — these are historical events.
    out.sort((a, b) => a.collectedDate.localeCompare(b.collectedDate));
    return out;
  }, [deposits]);
  // Local (IST) calendar date — gates the Generate Receipt CTA on the reminder.
  const todayLocal = format(new Date(), "yyyy-MM-dd");

  // Group periods into receipt rows by their CANONICAL billing period start,
  // across ALL deals on the account (COMBINE-01/02/03/04, phase 58.1) —
  // periods created by different Add-Plan deals that happen to share a
  // billing start date merge into one row, regardless of quoteGroupId. A
  // multi-space-type deal (e.g. Workstation + Private Suite booked together)
  // still collapses into a single combined row even when its lines started on
  // different dates — the whole deal is one purchase (operator request: "one
  // deal, one receipt") — because the canonical start is the deal's own
  // earliest line for that billing cycle, not a raw date match. Each
  // RECURRING cycle stays its own combined row (past, current, next never
  // collapse together) because each cycle canonicalises to its own start.
  // Periods with no quoteGroupId (solo / legacy) group by their billing start
  // date, as before. See `@/lib/period-grouping` for the algorithm. Click the
  // chevron to expand and act on individual periods.
  const dealPeriods = useMemo<DealPeriodGroup[]>(() => {
    return groupPeriodsForReceipts(periods).map((g) => ({
      id: `date-${g.periodStart}`,
      quoteGroupId: g.quoteGroupId,
      periodStart: g.periodStart,
      members: g.members,
      totalAmount: g.members.reduce((s, m) => s + Number(m.amount), 0),
      totalAmountPaid: g.members.reduce((s, m) => s + Number(m.amountPaid ?? 0), 0),
    }));
  }, [periods]);

  const [expandedDealKeys, setExpandedDealKeys] = useState<Set<string>>(new Set());
  function toggleDealExpand(key: string) {
    setExpandedDealKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Combined status across a deal-period group.
  function combinedStatus(g: DealPeriodGroup): "Paid" | "Partial" | "Unpaid" {
    if (g.members.every((m) => m.status === "Paid")) return "Paid";
    if (g.members.some((m) => m.status === "Paid" || m.status === "Partial")) return "Partial";
    return "Unpaid";
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
  }

  // Explicit "Generate Receipt" CTA — issues the per-period order receipt
  // (assigns the next serial + renders the PDF) without delivering it. The row
  // then flips to showing the Order ID and the View / share / record actions.
  async function onGenerate(p: PlanSchedulePeriod) {
    setGeneratingId(p.id);
    try {
      const r = await genReceipt.mutateAsync({ periodId: p.id, data: {} });
      invalidate();
      const id = r.receiptNumber ? formatOrderReceiptId(r.receiptNumber, p.periodStart) : "";
      toast({ title: id ? `Order receipt ${id} generated.` : "Order receipt generated." });
    } catch {
      toast({ title: "Couldn't generate the receipt.", variant: "destructive" });
    } finally {
      setGeneratingId(null);
    }
  }

  // Inline share icons: generate the receipt and deliver via the chosen channel.
  async function onReceipt(p: PlanSchedulePeriod, channel: "whatsapp" | "email") {
    try {
      const r = await genReceipt.mutateAsync({ periodId: p.id, data: { channel } });
      invalidate();
      toast({
        title:
          channel === "whatsapp"
            ? r.sent ? "Shared on WhatsApp." : "WhatsApp not configured or no number on file."
            : r.sent ? "Receipt emailed." : "Email not configured or no address on file.",
      });
    } catch {
      toast({ title: "Couldn't generate the receipt.", variant: "destructive" });
    }
  }

  // Bundle counterpart — share the combined receipt for a multi-membership
  // Plan deal via WhatsApp / Email. Same backend path the Generate flow
  // uses; passing channel triggers delivery and updates receiptSentAt on
  // each sibling period.
  async function onBundleReceipt(g: DealPeriodGroup, channel: "whatsapp" | "email") {
    try {
      const r = await genBundleReceipt.mutateAsync({
        id: accountId,
        data: {
          // Empty-string sentinel for a cross-deal / date-grouped row (no
          // single quoteGroupId) — inert server-side because periodIds is
          // always supplied and resolveBundleTarget never reads
          // quoteGroupId in that mode. Mirrors onGenerateBundle below.
          quoteGroupId: g.quoteGroupId ?? "",
          periodIds: g.members.map((m) => m.id),
          channel,
        },
      });
      invalidate();
      toast({
        title:
          channel === "whatsapp"
            ? r.sent ? "Shared on WhatsApp." : "WhatsApp not configured or no number on file."
            : r.sent ? "Receipt emailed." : "Email not configured or no address on file.",
      });
    } catch {
      toast({ title: "Couldn't share the bundle receipt.", variant: "destructive" });
    }
  }

  // One-click combined "Generate Receipt" for a multi-membership Plan deal —
  // issues the ONE combined order receipt covering every sibling period, no
  // selection popup. Mirrors onGenerate's try/finally + toast + invalidate.
  async function onGenerateBundle(g: DealPeriodGroup) {
    setGeneratingDealId(g.id);
    try {
      await genBundleReceipt.mutateAsync({
        id: accountId,
        data: {
          quoteGroupId: g.quoteGroupId ?? "",
          periodIds: g.members.map((m) => m.id),
        },
      });
      invalidate();
      toast({ title: "Combined order receipt generated." });
    } catch {
      toast({ title: "Couldn't generate the combined receipt.", variant: "destructive" });
    } finally {
      setGeneratingDealId(null);
    }
  }

  async function onReminder(p: PlanSchedulePeriod, value: string) {
    try {
      await updateReminder.mutateAsync({ periodId: p.id, data: { reminderDate: value || null } });
      invalidate();
    } catch {
      toast({ title: "Couldn't update the reminder date.", variant: "destructive" });
    }
  }

  // Combined-deal counterpart — the summary row shows ONE reminder date for
  // the whole deal (the first member's), so an edit has to land on every
  // sibling period behind it. Same fan-out the record-payment dialog uses for
  // its next-due write, so the deal never ends up with split reminders.
  async function onDealReminder(g: DealPeriodGroup, value: string) {
    setSavingReminderDealId(g.id);
    try {
      for (const m of g.members) {
        await updateReminder.mutateAsync({
          periodId: m.id,
          data: { reminderDate: value || null },
        });
      }
      invalidate();
    } catch {
      toast({ title: "Couldn't update the reminder date.", variant: "destructive" });
    } finally {
      setSavingReminderDealId(null);
    }
  }

  // Phase 28 PERIOD28-05: commit the edited period amount.
  async function onSaveAmount(p: PlanSchedulePeriod) {
    const amount = parseFloat(editAmountValue);
    if (!Number.isFinite(amount) || amount < 0) {
      toast({ title: "Enter a valid amount.", variant: "destructive" });
      return;
    }
    try {
      await updateAmount.mutateAsync({ periodId: p.id, data: { amount } });
      invalidate();
      setEditingAmount(null);
      toast({ title: "Period amount updated." });
    } catch (err) {
      const msg = (err as Error)?.message ?? "";
      // 422 period_immutable — period is already settled.
      toast({
        title: "Cannot edit this period's amount.",
        description: msg || "The period is already paid or has an issued receipt.",
        variant: "destructive",
      });
    }
  }

  // PERIOD28-05 disabled guard: period must not be Paid/Partial, have an issued receipt, or be already overridden.
  function isAmountEditDisabled(p: PlanSchedulePeriod): boolean {
    return (
      p.status === "Paid" ||
      p.status === "Partial" ||
      p.receiptNumber != null ||
      p.amountOverridden === true
    );
  }

  // Per-period row JSX, used both as a standalone row (singleton group)
  // and as an indented sub-row under an expanded Plan-deal summary row.
  // `isSubRow` softens the first column (indent + lighter typography)
  // so it visually nests under its parent.
  function renderPeriodRow(p: PlanSchedulePeriod, isSubRow: boolean) {
    return (
      <TableRow key={isSubRow ? `sub-${p.id}` : p.id} className={isSubRow ? "bg-muted/10" : undefined}>
        <TableCell>
          <div className={isSubRow ? "flex flex-col gap-0.5 pl-8" : "flex flex-col gap-0.5"}>
            {/* A combined-deal SUB-ROW shows only its space type (no individual
                "Plan #{id}" — the deal already carries the id on its summary
                row). A standalone/singleton row keeps "Plan #{id}". */}
            <span className={isSubRow ? "text-xs" : "text-sm font-medium"}>
              {isSubRow
                ? `${(p.quantity ?? 1) > 1 ? `${p.quantity}× ` : ""}${(p.planName ?? "Plan").replace(/_/g, " ")}`
                : `Plan #${p.membershipId}`}
            </span>
            <span className="text-xs text-muted-foreground">
              {isSubRow ? (
                durationByMembership.get(p.membershipId) != null
                  ? `${durationByMembership.get(p.membershipId)} mo`
                  : ""
              ) : (
                <>
                  {(p.quantity ?? 1) > 1 ? `${p.quantity}× ` : ""}
                  {(p.planName ?? "Plan").replace(/_/g, " ")}
                  {durationByMembership.get(p.membershipId) != null
                    ? ` · ${durationByMembership.get(p.membershipId)} mo`
                    : ""}
                </>
              )}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {format(new Date(p.periodStart), "dd MMM yyyy")}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {format(new Date(p.periodEnd), "dd MMM yyyy")}
        </TableCell>
        <TableCell className="text-right text-sm font-medium">
          {isSubRow ? (
            // Combined-deal breakdown row — the deal's total / paid / pending
            // live on the summary row above; don't repeat a per-space-type amount.
            <span className="text-muted-foreground">—</span>
          ) : editingAmount?.id === p.id ? (
            <div className="flex items-center justify-end gap-1">
              <Input
                type="number"
                min={0}
                value={editAmountValue}
                onChange={(e) => setEditAmountValue(e.target.value)}
                className="h-7 w-24 text-right text-xs"
                autoFocus
              />
              <Button
                size="sm"
                variant="default"
                className="h-7 px-2 text-xs"
                disabled={updateAmount.isPending}
                onClick={() => onSaveAmount(p)}
              >
                {updateAmount.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => setEditingAmount(null)}
              >
                ✕
              </Button>
            </div>
          ) : (
            /* Amount shown as three stacked parts in this ONE column: total
               (always), Paid/received (when > 0), Pending (when > 0). The
               received figure now lives ONLY here — removed from Status. */
            <div className="flex flex-col items-end gap-0.5">
              <div className="flex items-center justify-end gap-1">
                <span className={p.amountOverridden ? "text-blue-600" : ""}>
                  {formatINR(p.amount)}
                </span>
                {/* Edit-amount pencil only on non-sub rows where an override is
                    still possible (not Paid/Partial/receipted/pinned). */}
                {!isSubRow && !isAmountEditDisabled(p) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={-1}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground"
                          onClick={() => {
                            setEditingAmount(p);
                            setEditAmountValue(String(p.amount));
                          }}
                        >
                          <PenLine className="h-3.5 w-3.5" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Edit amount</TooltipContent>
                  </Tooltip>
                )}
              </div>
              {(() => {
                // Rent + unpaid security deposit, same figure as the receipt's
                // TOTAL TO BE PAID (see lib/period-money).
                const pm = periodMoney(p);
                return (
                  <>
                    {pm.depositDue > 0 && (
                      <span className="text-[10px] font-normal text-muted-foreground">
                        + {formatINR(pm.depositDue)} deposit
                      </span>
                    )}
                    {pm.paid > 0 && (
                      <span className="text-[10px] font-normal text-emerald-600">
                        Paid {formatINR(pm.paid)}
                      </span>
                    )}
                    {pm.pending > 0 && (
                      <span className="text-[10px] font-normal text-amber-600">
                        Pending {formatINR(pm.pending)}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </TableCell>
        <TableCell>
          {/* Combined-deal breakdown rows carry no per-line money state — the
              deal's status lives on the summary row above. */}
          {isSubRow ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <Badge
              variant={p.status === "Paid" ? "default" : p.status === "Partial" ? "outline" : "secondary"}
              className="w-fit text-[10px]"
            >
              {p.status}
            </Badge>
          )}
        </TableCell>
        <TableCell>
          {isSubRow ? (
            <span className="text-xs text-muted-foreground">
              {p.reminderDate ? format(new Date(p.reminderDate), "dd MMM yyyy") : "—"}
            </span>
          ) : (
            <Input
              type="date"
              value={p.reminderDate ?? ""}
              onChange={(e) => onReminder(p, e.target.value)}
              className="h-8 w-[140px] text-xs"
            />
          )}
        </TableCell>
        <TableCell className="text-right">
          <TooltipProvider delayDuration={200}>
            {isSubRow && p.receiptNumber ? (
              // Sub-row whose receipt was issued via the parent's combined
              // receipt — don't repeat the # + icons here; they live on the
              // merged Combined row above. Keep a tiny hint so the operator
              // can see the row IS covered, not "missing".
              <span className="text-[10px] text-muted-foreground">
                via combined #{p.receiptNumber}
              </span>
            ) : isSubRow ? (
              // Unbilled sub-row of a combined deal — all actions live on the
              // combined summary row above; no per-line Generate Receipt here.
              <span className="text-[10px] text-muted-foreground">
                Billed via combined deal
              </span>
            ) : p.receiptNumber ? (
              <div className="flex items-center justify-end gap-1.5">
                <span className="font-mono text-xs font-medium tabular-nums">
                  {formatOrderReceiptId(p.receiptNumber, p.periodStart)}
                </span>
                <div className="flex items-center gap-0.5">
                  <PlanScheduleIconButton
                    label="View Receipt"
                    icon={Eye}
                    onClick={() => setViewing(p)}
                  />
                  <PlanScheduleIconButton
                    label="Share on WhatsApp"
                    icon={MessageCircle}
                    onClick={() => onReceipt(p, "whatsapp")}
                  />
                  <PlanScheduleIconButton
                    label="Share on Email"
                    icon={Mail}
                    onClick={() => onReceipt(p, "email")}
                  />
                  <PlanScheduleIconButton
                    label={p.status === "Paid" ? "Fully paid · edit" : "Record payment"}
                    icon={BadgeCheck}
                    className={
                      p.status === "Paid"
                        ? "text-green-600"
                        : p.status === "Partial"
                          ? "text-amber-600"
                          : ""
                    }
                    onClick={() => setRecording(p)}
                  />
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={-1}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1.5 text-xs"
                        // A future reminder no longer BLOCKS the receipt — it
                        // only warns (tooltip below). Operators bill periods
                        // ahead of schedule on request; the reminder is a
                        // follow-up date, not a billing lock.
                        disabled={generatingId === p.id}
                        onClick={() => onGenerate(p)}
                      >
                        {generatingId === p.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Receipt className="h-3.5 w-3.5" />
                        )}
                        Generate Receipt
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!!p.reminderDate && p.reminderDate > todayLocal && (
                    <TooltipContent>
                      Ahead of the {format(new Date(p.reminderDate), "dd MMM yyyy")} reminder —
                      this bills the period early.
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            )}
          </TooltipProvider>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <PlanScheduleTile
          label="Days completed"
          value={summary ? String(summary.daysCompleted) : "—"}
          sub="since first plan"
        />
        <PlanScheduleTile
          label="Space types"
          value={summary ? String(summary.spaceTypeCount) : "—"}
          sub="distinct resource types"
        />
        <PlanScheduleTile
          label="Team members"
          value={summary ? String(summary.teamMembers) : "—"}
          sub={summary && summary.teamMembersGrowth > 0 ? `+${summary.teamMembersGrowth} last 30 days` : "active contacts"}
        />
        <PlanScheduleTile
          label="Per-head revenue"
          value={summary ? formatINR(summary.perHeadRevenue) : "—"}
          sub="scheduled / member"
        />
      </div>

      <Card>
        <CardHeader className="space-y-0 pb-3">
          <CardTitle className="text-base">Plan payment schedule</CardTitle>
          <p className="text-sm text-muted-foreground">
            One row per billing period for each recurring plan.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : periods.length === 0 && depositGroups.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No recurring plans assigned yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reminder Date</TableHead>
                  <TableHead className="w-10 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* 2026-06-30: one-time security-deposit collection rows at the
                    top of the schedule. Dated to the booking day; shows the
                    summed deposit per deal (multi-space-type deals collapse
                    via quoteGroupId). Non-billing — no actions; the deposit
                    is already Held on the membership and tracked in the
                    member_security_deposits ledger. */}
                {depositGroups.map((d) => (
                  <TableRow key={`deposit-${d.id}`} className="bg-emerald-50/40">
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">
                          Security deposit collected
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {d.items.length > 1
                            ? `${d.items.length} space types · refundable`
                            : "Refundable · one-time"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(d.collectedDate), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">—</TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {formatINR(d.totalAmount)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="w-fit border-emerald-300 bg-emerald-100 text-[10px] text-emerald-800">
                        Held
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">—</TableCell>
                    <TableCell className="text-right text-[10px] text-muted-foreground">
                      Refund via Restructure / Exit
                    </TableCell>
                  </TableRow>
                ))}
                {dealPeriods.flatMap((g) => {
                  const isMulti = g.members.length > 1;
                  const isExpanded = expandedDealKeys.has(g.id);
                  // For multi-member deals, render a merged summary row.
                  // The summary row sits in place of the first member's row;
                  // when expanded the individual member rows render below.
                  if (isMulti) {
                    const status = combinedStatus(g);
                    const first = g.members[0]!;
                    const summaryRow = (
                      <TableRow key={`deal-${g.id}`} className="bg-muted/20">
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-1 text-sm font-medium">
                              {groupPlanLabel(g.members)}
                              <button
                                type="button"
                                onClick={() => toggleDealExpand(g.id)}
                                className="p-0.5 rounded hover:bg-muted"
                                aria-label={isExpanded ? "Collapse deal" : "Expand deal"}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {g.members
                                .map(
                                  (m) =>
                                    `${(m.quantity ?? 1) > 1 ? `${m.quantity}× ` : ""}${(m.planName ?? "Plan").replace(/_/g, " ")}${
                                      durationByMembership.get(m.membershipId) != null
                                        ? ` · ${durationByMembership.get(m.membershipId)} mo`
                                        : ""
                                    }`,
                                )
                                .join(" | ")}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(g.periodStart), "dd MMM yyyy")}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(first.periodEnd), "dd MMM yyyy")}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {/* Three stacked parts in one column: total, Paid, Pending. */}
                          {(() => {
                            // Pending includes any unpaid security deposit so it matches
                            // the receipt's TOTAL TO BE PAID (see lib/period-money).
                            const gm = sumPeriodMoney(g.members);
                            return (
                              <div className="flex flex-col items-end gap-0.5">
                                <span>{formatINR(g.totalAmount)}</span>
                                {gm.depositDue > 0 && (
                                  <span className="text-[10px] font-normal text-muted-foreground">
                                    + {formatINR(gm.depositDue)} deposit
                                  </span>
                                )}
                                {gm.paid > 0 && (
                                  <span className="text-[10px] font-normal text-emerald-600">
                                    Paid {formatINR(gm.paid)}
                                  </span>
                                )}
                                {gm.pending > 0 && (
                                  <span className="text-[10px] font-normal text-amber-600">
                                    Pending {formatINR(gm.pending)}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              status === "Paid"
                                ? "default"
                                : status === "Partial"
                                ? "outline"
                                : "secondary"
                            }
                            className="w-fit text-[10px]"
                          >
                            {status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {/* Editable on the deal row too — the write fans out
                              to every sibling period (onDealReminder), so the
                              deal keeps ONE reminder date. Mirrors the solo
                              row's input. */}
                          <Input
                            type="date"
                            value={first.reminderDate ?? ""}
                            disabled={savingReminderDealId === g.id}
                            onChange={(e) => onDealReminder(g, e.target.value)}
                            className="h-8 w-[140px] text-xs"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          {/* Combined Receipt — generates ONE PDF covering all
                              sibling memberships in this period. A mixed row
                              (some receipts already issued) still acts as one:
                              Generate Receipt unifies the number. No expand
                              needed (operator, 2026-11-03). */}
                          {(() => {
                            const allReceipts = g.members.every((m) => m.receiptNumber);
                            if (allReceipts) {
                              // All siblings share the same combined-receipt
                              // number + PDF (persisted by bundle-receipt
                              // endpoint). Render the same icon cluster a
                              // solo period gets when its receipt is issued.
                              return (
                                <div className="flex items-center justify-end gap-1.5">
                                  <span className="font-mono text-xs font-medium tabular-nums">
                                    {first.receiptNumber}
                                  </span>
                                  <div className="flex items-center gap-0.5">
                                    <PlanScheduleIconButton
                                      label="View Combined Receipt"
                                      icon={Eye}
                                      onClick={() => setViewingBundle(g)}
                                    />
                                    <PlanScheduleIconButton
                                      label="Share on WhatsApp"
                                      icon={MessageCircle}
                                      onClick={() => onBundleReceipt(g, "whatsapp")}
                                    />
                                    <PlanScheduleIconButton
                                      label="Share on Email"
                                      icon={Mail}
                                      onClick={() => onBundleReceipt(g, "email")}
                                    />
                                    <PlanScheduleIconButton
                                      label={combinedStatus(g) === "Paid" ? "Fully paid · edit" : "Record payment"}
                                      icon={BadgeCheck}
                                      className={
                                        combinedStatus(g) === "Paid"
                                          ? "text-green-600"
                                          : combinedStatus(g) === "Partial"
                                            ? "text-amber-600"
                                            : ""
                                      }
                                      onClick={() => setRecordingGroup(g)}
                                    />
                                  </div>
                                </div>
                              );
                            }
                            // MIXED (some siblings already carry a receipt number, some
                            // don't) is NOT a reason to send the operator into the
                            // chevron: plans that share a billing date are ONE row and
                            // ONE order receipt (operator rule, 2026-11-03). Fall
                            // through to the combined Generate Receipt action — the
                            // bundle endpoint reuses a shared number when every period
                            // has the same one, else mints one and stamps it on all.
                            // No siblings have a receipt yet — offer the
                            // combined-receipt action. A reminder still in the
                            // future WARNS but no longer blocks: the deal can
                            // be billed ahead of schedule on request.
                            const isEarly =
                              first.reminderDate && first.reminderDate > todayLocal;
                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={-1}>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 gap-1.5 text-xs"
                                      disabled={generatingDealId === g.id}
                                      onClick={() => onGenerateBundle(g)}
                                    >
                                      {generatingDealId === g.id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <Receipt className="h-3.5 w-3.5" />
                                      )}
                                      Generate Receipt
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {isEarly && (
                                  <TooltipContent>
                                    Ahead of the{" "}
                                    {format(new Date(first.reminderDate!), "dd MMM yyyy")} reminder
                                    — this bills the deal early.
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                    if (!isExpanded) return [summaryRow];
                    // Expanded — render the summary row + indented per-member rows.
                    return [summaryRow, ...g.members.map((p) => renderPeriodRow(p, true))];
                  }
                  // Singleton group — render the period row as before.
                  return [renderPeriodRow(g.members[0]!, false)];
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payment receiving ledger — one row per payment received. */}
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Payment ledger</CardTitle>
            <p className="text-sm text-muted-foreground">
              {paymentGroups.length} payment{paymentGroups.length === 1 ? "" : "s"} received
            </p>
          </div>
          {summary?.nextPaymentDate && (
            <div className="text-right">
              <p className="text-[11px] text-muted-foreground">Next payment due</p>
              <p className="text-sm font-semibold">
                {format(new Date(summary.nextPaymentDate), "dd MMM yyyy")}
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No payments recorded yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Paid On</TableHead>
                  <TableHead>Plan / Period</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Next Payment</TableHead>
                  <TableHead>Proof</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paymentGroups.map((g) => {
                  // Representative payment (first line) drives the shared cells;
                  // a combined deal payment shares paidAt / period / mode /
                  // nextPayment / proof across its lines.
                  const first = g.items[0]!;
                  const proofItem = g.items.find((p) => p.proofUrl) ?? first;
                  return (
                    <TableRow key={g.key}>
                      <TableCell className="text-xs">
                        {format(new Date(first.paidAt), "dd MMM yyyy, h:mm a")}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-col">
                          {/* A DEAL payment — a combined multi-line payment OR a
                              single deposit/credit-note adjustment on a deal
                              line — is labelled by the DEAL, never the space
                              type. Standalone plans keep name + "Plan #{id}". */}
                          {g.isDeal || g.isGroup ? (
                            <span className="font-medium">
                              Plan #{g.dealId}
                              {g.isGroup && (
                                <span className="ml-1.5 font-normal text-muted-foreground">
                                  Combined · {g.items.length} space types
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="font-medium">
                              {(first.planName ?? "Plan").replace(/_/g, " ")}
                              {first.membershipId != null && (
                                <span className="ml-1.5 font-normal text-muted-foreground">
                                  Plan #{first.membershipId}
                                </span>
                              )}
                            </span>
                          )}
                          <span className="text-muted-foreground">
                            {first.periodStart ? format(new Date(first.periodStart), "dd MMM") : "—"} →{" "}
                            {first.periodEnd ? format(new Date(first.periodEnd), "dd MMM yyyy") : "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">{formatINR(g.total)}</TableCell>
                      <TableCell className="text-xs">
                        {first.paymentMode ? first.paymentMode.replace(/_/g, " ") : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {first.nextPaymentDate ? format(new Date(first.nextPaymentDate), "dd MMM yyyy") : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {proofItem.proofUrl ? (
                          <a href={proofItem.proofUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                            View
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Phase 36 — Credit Notes section. */}
      <Card>
        <CardHeader className="space-y-0 pb-3">
          <CardTitle className="text-base">Credit Notes</CardTitle>
          <p className="text-sm text-muted-foreground">
            GST credit notes issued for this account (e.g. from bundle restructures).
          </p>
        </CardHeader>
        <CardContent>
          <CreditNotesList accountId={accountId} />
        </CardContent>
      </Card>

      {/* Security Deposits — settlement actions mirror Credit Notes. */}
      <Card>
        <CardHeader className="space-y-0 pb-3">
          <CardTitle className="text-base">Security Deposits</CardTitle>
          <p className="text-sm text-muted-foreground">
            Refundable deposits held against active plans. Mark Refunded
            (after lock-in) or Mark Adjusted to apply against the next plan.
          </p>
        </CardHeader>
        <CardContent>
          <SecurityDepositsList accountId={accountId} />
        </CardContent>
      </Card>

      {viewing && (
        <ReceiptModal
          accountId={accountId}
          period={viewing}
          onClose={() => setViewing(null)}
          onRecord={() => {
            const p = viewing;
            setViewing(null);
            setRecording(p);
          }}
        />
      )}
      {viewingBundle && (
        <BundleReceiptModal
          accountId={accountId}
          group={viewingBundle}
          onClose={() => setViewingBundle(null)}
          onRecord={() => {
            const g = viewingBundle;
            setViewingBundle(null);
            setRecordingGroup(g);
          }}
        />
      )}
      {recording && (
        <RecordPaymentDialog
          accountId={accountId}
          period={recording}
          firstPeriodDeposit={
            recording.periodIndex === 0
              ? depositByMembership.get(recording.membershipId) ?? 0
              : 0
          }
          heldDeposits={heldDeposits}
          creditNotes={applicableCreditNotes}
          onClose={() => setRecording(null)}
        />
      )}
      {recordingGroup && (
        <GroupRecordPaymentDialog
          accountId={accountId}
          group={recordingGroup}
          depositByMembership={depositByMembership}
          heldDeposits={heldDeposits}
          creditNotes={applicableCreditNotes}
          onClose={() => setRecordingGroup(null)}
        />
      )}
    </div>
  );
}

function PlanScheduleIconButton({
  label,
  icon: Icon,
  onClick,
  className,
}: {
  label: string;
  icon: typeof Eye;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" className={`h-7 w-7 ${className ?? ""}`} onClick={onClick}>
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// Server reads a `documentType` flag on the receipt body to render the TAX
// INVOICE variant; it isn't in the generated client schema yet, so extend it.
type PeriodDocBody = GeneratePlanPeriodReceiptBody & {
  documentType?: "receipt" | "invoice";
  // Optional back-dated invoice date (YYYY-MM-DD) for a past-month period; the
  // server derives the #NN/MMYYYY number from it. Omitted → system-dated (today).
  invoiceDate?: string;
  // Optional operator-set invoice number (to match an old manual series, e.g.
  // 02/042026). Takes precedence over the auto #NN/MMYYYY. Omitted → auto.
  invoiceNumber?: string;
  // Explicit operator re-date/re-number of an ALREADY-issued invoice. Without
  // it the server returns the issued invoice's frozen number + date.
  redate?: boolean;
};

// Document preview modal — Receipt | Invoice tabs. The Order Receipt is generated
// on open (so amounts reflect the latest payment); the Tax Invoice is generated
// lazily and only when the period is fully paid. Offers Download / WhatsApp /
// Email (acting on the active tab) and Record Payment.
function ReceiptModal({
  accountId,
  period,
  onClose,
  onRecord,
}: {
  accountId: number;
  period: PlanSchedulePeriod;
  onClose: () => void;
  onRecord: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const genReceipt = useGeneratePlanPeriodReceipt();

  // The Tax Invoice is a GST document: available only once the period is fully
  // paid AND GST actually applies. A non-GST period has no tax invoice — the
  // order receipt is the valid artifact, so the Invoice tab stays disabled.
  const invoiceEnabled = period.status === "Paid" && period.gstApplicable;
  const [tab, setTab] = useState<"receipt" | "invoice">("receipt");

  // Back-dating: a period that STARTED in a past calendar month (IST) shows an
  // editable invoice date so the operator can override it; the #NN/MMYYYY
  // number follows that date's month. By default every tax invoice is dated on
  // the payment date (server-side).
  const istToday10 = new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  const periodMonthIsPast = period.periodStart.slice(0, 7) < istToday10.slice(0, 7);
  // Empty until the server answers: a new tax invoice is dated on the payment
  // date server-side, and the issued date flows back via syncIssued(). The
  // operator can still edit it for a back-dated period.
  const [invoiceDate, setInvoiceDate] = useState<string>("");

  // Order-receipt document state.
  const [receiptPdfUrl, setReceiptPdfUrl] = useState<string | null>(period.receiptPdfUrl ?? null);
  // Operator-editable order receipt number. Seeded from any number already issued
  // for this period; the auto-generated value flows back from the first render.
  const [receiptNumber, setReceiptNumber] = useState<string>(period.receiptNumber ?? "");

  // Tax-invoice document state (rendered lazily when the Invoice tab opens).
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [invoiceLoaded, setInvoiceLoaded] = useState(false);

  const [busy, setBusy] = useState<null | "load" | "update" | "whatsapp" | "email">("load");

  // Generate the order receipt on open.
  useEffect(() => {
    let cancelled = false;
    genReceipt
      .mutateAsync({ periodId: period.id, data: {} })
      .then((r) => {
        if (cancelled) return;
        setReceiptPdfUrl(r.pdfUrl ?? null);
        if (r.receiptNumber) setReceiptNumber(r.receiptNumber);
      })
      .catch(() => { if (!cancelled) toast({ title: "Couldn't load the receipt.", variant: "destructive" }); })
      .finally(() => { if (!cancelled) setBusy(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.id]);

  // The issued invoice's frozen date/number as last returned by the server —
  // lets Share tell an operator edit apart from an unchanged re-send.
  const [issued, setIssued] = useState<{ date: string; number: string }>({ date: "", number: "" });
  function syncIssued(r: { receiptNumber?: string | null }) {
    const d = (r as { invoiceDate?: string }).invoiceDate ?? "";
    if (d) setInvoiceDate(d);
    setIssued({ date: d || invoiceDate, number: r.receiptNumber ?? "" });
  }

  // Render the tax invoice the first time the Invoice tab is opened.
  async function loadInvoice() {
    if (invoiceLoaded || !invoiceEnabled) return;
    setBusy("load");
    try {
      const body: PeriodDocBody = {
        documentType: "invoice",
        ...(invoiceDate ? { invoiceDate } : {}),
      };
      const r = await genReceipt.mutateAsync({ periodId: period.id, data: body });
      setInvoicePdfUrl(r.pdfUrl ?? null);
      if (r.receiptNumber) setInvoiceNumber(r.receiptNumber);
      syncIssued(r);
      setInvoiceLoaded(true);
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
    } catch (err) {
      // The server refuses an invoice until rent AND any security deposit on it
      // are fully received — say which, rather than a generic failure.
      const data = (err as { data?: { detail?: string; depositPending?: number } } | null)?.data;
      if (data?.detail === "security_deposit_unpaid") {
        toast({
          title: "Security deposit is not fully paid.",
          description: `Collect the remaining ${formatINR(Number(data.depositPending ?? 0))} deposit to generate the tax invoice.`,
          variant: "destructive",
        });
        setTab("receipt");
      } else {
        toast({ title: "Couldn't generate the invoice.", variant: "destructive" });
      }
    } finally {
      setBusy(null);
    }
  }

  // Regenerate the tax-invoice preview with the (edited) back-dated invoice date.
  // The server re-mints the #NN/MMYYYY number for the chosen month when it differs.
  async function updateInvoice() {
    setBusy("update");
    try {
      const body: PeriodDocBody = {
        documentType: "invoice",
        ...(invoiceDate ? { invoiceDate } : {}),
        ...(invoiceNumber.trim() ? { invoiceNumber: invoiceNumber.trim() } : {}),
        redate: true,
      };
      const r = await genReceipt.mutateAsync({ periodId: period.id, data: body });
      if (r.pdfUrl) setInvoicePdfUrl(r.pdfUrl);
      if (r.receiptNumber) setInvoiceNumber(r.receiptNumber);
      syncIssued(r);
      setInvoiceLoaded(true);
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
    } catch {
      toast({ title: "Couldn't update the invoice date.", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  function onTabChange(v: string) {
    const next = v === "invoice" ? "invoice" : "receipt";
    setTab(next);
    if (next === "invoice") void loadInvoice();
  }

  // Regenerate the receipt preview with the (possibly edited) receipt number.
  async function updatePreview() {
    setBusy("update");
    try {
      const r = await genReceipt.mutateAsync({
        periodId: period.id,
        data: receiptNumber.trim() ? { receiptNumber: receiptNumber.trim() } : {},
      });
      if (r.pdfUrl) setReceiptPdfUrl(r.pdfUrl);
      if (r.receiptNumber) setReceiptNumber(r.receiptNumber);
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
    } catch {
      toast({ title: "Couldn't update the receipt number.", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  // Share the document for the active tab via the chosen channel.
  async function share(channel: "whatsapp" | "email") {
    setBusy(channel);
    const sharingInvoice = tab === "invoice";
    try {
      const body: PeriodDocBody = sharingInvoice
        ? {
            channel,
            documentType: "invoice",
            ...(invoiceDate ? { invoiceDate } : {}),
            ...(invoiceNumber.trim() ? { invoiceNumber: invoiceNumber.trim() } : {}),
            // Only re-date when the operator edited the date/number in the modal.
            ...(invoiceDate !== issued.date || invoiceNumber.trim() !== issued.number
              ? { redate: true }
              : {}),
          }
        : { channel, ...(receiptNumber.trim() ? { receiptNumber: receiptNumber.trim() } : {}) };
      const r = await genReceipt.mutateAsync({ periodId: period.id, data: body });
      if (r.pdfUrl) {
        if (sharingInvoice) setInvoicePdfUrl(r.pdfUrl);
        else setReceiptPdfUrl(r.pdfUrl);
      }
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      const docLabel = sharingInvoice ? "Invoice" : "Receipt";
      toast({
        title:
          channel === "whatsapp"
            ? r.sent ? "Shared on WhatsApp." : "WhatsApp not configured or no number on file."
            : r.sent ? `${docLabel} emailed.` : "Email not configured or no address on file.",
      });
    } catch {
      toast({ title: "Couldn't share the document.", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  const isInvoice = tab === "invoice";
  const activePdfUrl = isInvoice ? invoicePdfUrl : receiptPdfUrl;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <DialogTitle>
                {isInvoice ? "Tax Invoice" : "Order Receipt"} — {(period.planName ?? "Plan").replace(/_/g, " ")}
              </DialogTitle>
              <DialogDescription>
                {format(new Date(period.periodStart), "dd MMM yyyy")} → {format(new Date(period.periodEnd), "dd MMM yyyy")} · {period.cycleLabel}
              </DialogDescription>
            </div>
            {/* Receipt vs Invoice. The Invoice is only available once the period
                is fully paid (Marked paid, no balance left). */}
            <Tabs value={tab} onValueChange={onTabChange}>
              <TabsList>
                <TabsTrigger value="receipt">Receipt</TabsTrigger>
                <TabsTrigger
                  value="invoice"
                  disabled={!invoiceEnabled}
                  title={
                    invoiceEnabled
                      ? undefined
                      : !period.gstApplicable
                        ? "No GST applies — a tax invoice requires GST; use the receipt"
                        : "Available once the period is fully paid"
                  }
                >
                  Tax Invoice
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </DialogHeader>

        {/* Document number row. Receipt: editable order-receipt number + Update.
            Invoice: the auto #NN/MMYYYY series (current month), shown read-only. */}
        {isInvoice ? (
          <div className="space-y-2">
            {/* Tax invoice number is operator-editable — set it to match an old
                manual series (e.g. 02/042026). Blank → server auto #NN/MMYYYY. */}
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Tax invoice no.</Label>
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Auto (e.g. 02/042026)"
                  className="h-9 font-mono"
                />
              </div>
              <Button variant="outline" size="sm" disabled={!!busy} onClick={updateInvoice}>
                {busy === "update" ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                )}
                Update
              </Button>
            </div>
            {/* Back-dated period → editable invoice date (default = period start).
                Current-month / future periods stay system-dated (today). */}
            {periodMonthIsPast && (
              <div className="space-y-1">
                <Label className="text-xs">Invoice date</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  max={istToday10}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="h-9"
                />
                <p className="text-[11px] text-muted-foreground">
                  Past period — set the date/number to match your records, then Update.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Order receipt no.</Label>
              <Input
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                placeholder="Auto (e.g. 01/062026)"
                className="h-9 font-mono"
              />
            </div>
            <Button variant="outline" size="sm" disabled={!!busy} onClick={updatePreview}>
              {busy === "update" ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 h-4 w-4" />
              )}
              Update
            </Button>
          </div>
        )}

        {/* Full preview — the generated PDF with the browser's PDF viewer chrome
            hidden (#toolbar=0 removes toolbar / zoom / page nav). */}
        <div className="h-[62vh] overflow-hidden rounded-md border bg-muted/10">
          {activePdfUrl ? (
            <iframe
              key={activePdfUrl}
              src={`${activePdfUrl}#toolbar=0&navpanes=0&view=FitH`}
              title={isInvoice ? "Tax invoice" : "Order receipt"}
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {busy === "load" ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating {isInvoice ? "invoice" : "receipt"}…
                </span>
              ) : (
                "No preview available."
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-start">
          <Button variant="outline" size="sm" disabled={!activePdfUrl} onClick={() => activePdfUrl && window.open(activePdfUrl, "_blank", "noopener")}>
            <Download className="mr-1.5 h-4 w-4" /> Download
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => share("whatsapp")}>
            <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => share("email")}>
            <Mail className="mr-1.5 h-4 w-4" /> Email
          </Button>
          <Button size="sm" className="sm:ml-auto" onClick={onRecord}>
            <BadgeCheck className="mr-1.5 h-4 w-4" />
            {period.status === "Paid" ? "Edit Payment" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Viewer for a merged Plan-deal row's COMBINED documents. Regenerates the
// bundle PDF on open via the bundle-receipt endpoint (passing every sibling
// periodId), so the rendered PDF always contains a line per space type —
// independent of any stale per-period PDFs that may be on individual rows.
// A Receipt | Tax Invoice tab lets the operator also generate the GROUPED tax
// invoice for the whole deal (one #NN/MMYYYY covering every space type),
// available once every sibling period is fully paid (server also enforces this,
// returning 422 bundle_not_fully_paid otherwise).
function BundleReceiptModal({
  accountId,
  group,
  onClose,
  onRecord,
}: {
  accountId: number;
  group: DealPeriodGroup;
  onClose: () => void;
  onRecord: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const genReceipt = useGenerateBundleReceipt();
  const genInvoice = useGenerateBundleInvoice();

  const allPaid = group.members.every((m) => m.status === "Paid");
  const [tab, setTab] = useState<"receipt" | "invoice">("receipt");

  // Order-receipt document state.
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [receiptNumber, setReceiptNumber] = useState<string>(group.members[0]?.receiptNumber ?? "");
  // Tax-invoice document state (lazy — only generated when the Invoice tab opens).
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [invoiceLoaded, setInvoiceLoaded] = useState(false);
  const [busy, setBusy] = useState<null | "load" | "invoice" | "whatsapp" | "email">("load");

  const periodIds = group.members.map((m) => m.id);

  useEffect(() => {
    let cancelled = false;
    genReceipt
      .mutateAsync({
        id: accountId,
        data: {
          quoteGroupId: group.quoteGroupId ?? "",
          periodIds,
        },
      })
      .then((r) => {
        if (cancelled) return;
        setPdfUrl(r.pdfUrl ?? null);
        if (r.receiptNumber) setReceiptNumber(r.receiptNumber);
        queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      })
      .catch(() => {
        if (!cancelled) toast({ title: "Couldn't load the combined receipt.", variant: "destructive" });
      })
      .finally(() => { if (!cancelled) setBusy(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  // Lazily generate the combined tax invoice the first time the Invoice tab opens.
  async function loadInvoice() {
    if (invoiceLoaded || !allPaid) return;
    setBusy("invoice");
    try {
      const r = await genInvoice.mutateAsync({
        id: accountId,
        data: { quoteGroupId: group.quoteGroupId ?? "", periodIds },
      });
      setInvoicePdfUrl(r.pdfUrl ?? null);
      setInvoiceNumber(r.invoiceNumber ?? "");
      setInvoiceLoaded(true);
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
    } catch (err) {
      const msg = (err as Error)?.message ?? "";
      toast({
        title: "Couldn't generate the combined invoice.",
        description: msg.includes("not_fully_paid") ? "All space types must be fully paid first." : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  }

  async function share(channel: "whatsapp" | "email") {
    setBusy(channel);
    try {
      if (tab === "invoice") {
        const r = await genInvoice.mutateAsync({
          id: accountId,
          data: { quoteGroupId: group.quoteGroupId ?? "", periodIds, channel },
        });
        if (r.pdfUrl) setInvoicePdfUrl(r.pdfUrl);
        if (r.invoiceNumber) setInvoiceNumber(r.invoiceNumber);
        setInvoiceLoaded(true);
        queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
        toast({
          title:
            channel === "whatsapp"
              ? r.sent ? "Invoice shared on WhatsApp." : "WhatsApp not configured or no number on file."
              : r.sent ? "Invoice emailed." : "Email not configured or no address on file.",
        });
      } else {
        const r = await genReceipt.mutateAsync({
          id: accountId,
          data: { quoteGroupId: group.quoteGroupId ?? "", periodIds, channel },
        });
        if (r.pdfUrl) setPdfUrl(r.pdfUrl);
        queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
        toast({
          title:
            channel === "whatsapp"
              ? r.sent ? "Shared on WhatsApp." : "WhatsApp not configured or no number on file."
              : r.sent ? "Receipt emailed." : "Email not configured or no address on file.",
        });
      }
    } catch {
      toast({ title: `Couldn't share the combined ${tab === "invoice" ? "invoice" : "receipt"}.`, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  const periodStart = group.members[0]?.periodStart ?? group.periodStart;
  const periodEnd = group.members[0]?.periodEnd ?? group.periodStart;
  const activePdf = tab === "invoice" ? invoicePdfUrl : pdfUrl;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {tab === "invoice" ? "Tax Invoice" : "Order Receipt"} — {group.members.length} space types
          </DialogTitle>
          <DialogDescription>
            {format(new Date(periodStart), "dd MMM yyyy")} → {format(new Date(periodEnd), "dd MMM yyyy")} ·{" "}
            {group.members.map((m) => (m.planName ?? "Plan").replace(/_/g, " ")).join(" + ")}
          </DialogDescription>
        </DialogHeader>

        {/* Receipt vs Tax Invoice — the invoice covers the whole deal in ONE
            document (grouped by quote group). Disabled until every space type is
            fully paid. */}
        <Tabs
          value={tab}
          onValueChange={(v) => {
            const next = v === "invoice" ? "invoice" : "receipt";
            setTab(next);
            if (next === "invoice") void loadInvoice();
          }}
        >
          <TabsList>
            <TabsTrigger value="receipt">Receipt</TabsTrigger>
            <TabsTrigger value="invoice" disabled={!allPaid} title={!allPaid ? "All space types must be fully paid" : undefined}>
              Tax Invoice
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">{tab === "invoice" ? "Tax invoice no." : "Order receipt no."}</Label>
            <Input value={tab === "invoice" ? (invoiceNumber ? `#${invoiceNumber}` : "") : receiptNumber} readOnly className="h-9 font-mono" />
          </div>
        </div>

        <div className="h-[62vh] overflow-hidden rounded-md border bg-muted/10">
          {activePdf ? (
            <iframe
              key={activePdf}
              src={`${activePdf}#toolbar=0&navpanes=0&view=FitH`}
              title={tab === "invoice" ? "Combined tax invoice" : "Combined order receipt"}
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {busy === "load" || busy === "invoice" ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating combined {tab === "invoice" ? "invoice" : "receipt"}…
                </span>
              ) : tab === "invoice" && !allPaid ? (
                "Record payment for every space type to unlock the combined tax invoice."
              ) : (
                "No preview available."
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-start">
          <Button
            variant="outline"
            size="sm"
            disabled={!activePdf}
            onClick={() => activePdf && window.open(activePdf, "_blank", "noopener")}
          >
            <Download className="mr-1.5 h-4 w-4" /> Download
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy || (tab === "invoice" && !activePdf)} onClick={() => share("whatsapp")}>
            <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy || (tab === "invoice" && !activePdf)} onClick={() => share("email")}>
            <Mail className="mr-1.5 h-4 w-4" /> Email
          </Button>
          <Button size="sm" className="sm:ml-auto" onClick={onRecord}>
            <BadgeCheck className="mr-1.5 h-4 w-4" />
            {allPaid ? "Edit Payment" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PAYMENT_MODES = ["Cash", "UPI", "Bank_Transfer", "Card", "Cheque"] as const;

/** 2 dp rounding — shared by both Record Payment dialogs (money-correctness). */
function round2(n: number): number {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

/**
 * Pure sequential-fill allocator for the combined (deal) Record Payment form.
 * Walks `lines` IN CREATION ORDER (caller must not re-sort) and consumes
 * `entered` (the ONE bank amount the operator typed) line-by-line: each line's
 * `need` = rentRemaining + deposit is filled to full before the next line gets
 * anything. Leftover lines get 0 (stay unpaid/partial). Used for BOTH the save()
 * mutation loop and the live allocation preview so they can never drift.
 */
function sequentialAllocate<T extends { rentRemaining: number; deposit: number }>(
  lines: T[],
  entered: number,
): Array<{ line: T; give: number; rentPortion: number; depositPortion: number }> {
  let remainingBudget = Math.max(0, round2(entered));
  const out: Array<{ line: T; give: number; rentPortion: number; depositPortion: number }> = [];
  for (const line of lines) {
    const need = round2(line.rentRemaining + line.deposit);
    if (need <= 0) continue;
    const give = round2(Math.min(remainingBudget, need));
    if (give <= 0) break;
    const rentPortion = round2(Math.min(give, line.rentRemaining));
    const depositPortion = round2(Math.max(0, give - rentPortion));
    out.push({ line, give, rentPortion, depositPortion });
    remainingBudget = round2(remainingBudget - give);
  }
  return out;
}

// RecordPaymentDialog — extracted to components/billing/record-payment-dialog.tsx
// (Phase 38, imported above) so the Member profile's Collect-payment surface
// can reuse it. Usage below (single-period call site) is unchanged; no
// additionalPeriods passed, so behavior is byte-identical to before.

// Grouped Record Payment — settles a combined deal (one quote group) with the
// SAME form model as the single-period RecordPaymentDialog: one "Total to
// collect", a Partial toggle, an editable Bank-amount input (defaults to the
// full total), Mark-unpaid, and proof upload/replace/view. Internal allocation
// across the deal's periods is SEQUENTIAL FILL (operator doesn't choose): the
// entered bank amount pays each period (in creation order) to full — rent then
// its first-period deposit — before the next period gets anything. Leftover
// periods stay unpaid/partial. Reuses the single dialog's exact per-period
// rent-then-deposit split (bankAmount → server) for lines with a deposit, and
// the plain amountPaid running-total path for lines without one.
function GroupRecordPaymentDialog({
  accountId,
  group,
  depositByMembership,
  heldDeposits = [],
  creditNotes = [],
  onClose,
}: {
  accountId: number;
  group: DealPeriodGroup;
  depositByMembership: Map<number, number>;
  /** Account's Held security deposits, offered as a funding source toward the
   *  first payable period's rent due (Quick task 9 — Apply held deposit). */
  heldDeposits?: HeldDeposit[];
  /** Account's Issued/Adjusted credit notes, offered as a funding source
   *  toward the first payable period's rent due (Quick task 9 — Apply
   *  credit note). */
  creditNotes?: CreditNote[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const markPaid = useMarkPlanPeriodPaid();
  const updateReminder = useUpdatePlanPeriodReminder();
  const applyDeposit = useApplyDepositToPeriod();
  const applyCreditNote = useApplyCreditNoteToPeriod();
  // Per-period amountPaid overrides applied after a deposit-apply /
  // credit-note-apply click (the server round-trip settles the source +
  // records rent atomically); merged on top of `group.members` so "Total to
  // collect" reflects it immediately without closing the dialog.
  const [paidOverrides, setPaidOverrides] = useState<Map<number, number>>(new Map());
  const [applyingDepositId, setApplyingDepositId] = useState<number | null>(null);
  const [applyingCreditNoteId, setApplyingCreditNoteId] = useState<number | null>(null);

  // Per-member settlement lines: rent still owed + the one-time deposit billed on
  // that membership's first period. Fully-paid members contribute 0 and are
  // excluded from `payable` (the sequential-fill walk) but stay visible in the
  // breakdown card. IMPORTANT: creation order (group.members order) — never
  // re-sort, the sequential fill depends on it.
  const lines = group.members.map((m) => {
    const amountPaid = paidOverrides.get(m.id) ?? m.amountPaid ?? 0;
    const rentRemaining = round2(Math.max(0, m.amount - amountPaid));
    const deposit = m.periodIndex === 0 ? round2(Math.max(0, depositByMembership.get(m.membershipId) ?? 0)) : 0;
    return { period: { ...m, amountPaid }, rentRemaining, deposit };
  });
  const payable = lines.filter((l) => l.rentRemaining > 0 || l.deposit > 0);
  const anyPaid = lines.some((l) => (l.period.amountPaid ?? 0) > 0);
  const totalRent = round2(payable.reduce((s, l) => s + l.rentRemaining, 0));
  const totalDeposit = round2(payable.reduce((s, l) => s + l.deposit, 0));
  const totalToCollect = round2(totalRent + totalDeposit);

  const [partial, setPartial] = useState(false);
  const [amount, setAmount] = useState<string>(String(totalToCollect > 0 ? totalToCollect : 0));
  const [mode, setMode] = useState<string>(group.members[0]?.paymentMode ?? "UPI");
  const [when, setWhen] = useState<string>(() => new Date().toISOString().slice(0, 16));
  const [proofUrl, setProofUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [nextDue, setNextDue] = useState<string>("");

  // Live sequential-allocation preview — the SAME pure function drives both
  // the on-screen preview and the save() loop so they can't drift.
  const entered = Math.max(0, Number(amount) || 0);
  const allocation = sequentialAllocate(payable, entered);
  const allocatedTotal = round2(allocation.reduce((s, a) => s + a.give, 0));
  const balanceAfter = round2(Math.max(0, totalToCollect - allocatedTotal));

  // Apply a Held deposit toward the FIRST payable period's rent-due
  // (sequential, matching the combined form's fill order), then let the
  // normal sequential-fill collect the rest of the cash.
  async function onApplyDeposit(depositId: number) {
    const target = payable[0];
    if (!target) return;
    setApplyingDepositId(depositId);
    try {
      const updated = await applyDeposit.mutateAsync({
        id: accountId,
        depositId,
        data: { periodId: target.period.id },
      });
      setPaidOverrides((prev) => {
        const next = new Map(prev);
        next.set(updated.id, updated.amountPaid);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      toast({ title: "Held deposit applied toward rent due." });
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      toast({
        title: "Couldn't apply the deposit.",
        description: code === "nothing_due" ? "Nothing due on this period." : undefined,
        variant: "destructive",
      });
    } finally {
      setApplyingDepositId(null);
    }
  }

  // Apply a WHOLE credit note toward the FIRST payable period's rent-due
  // (sequential, matching the combined form's fill order).
  async function onApplyCreditNote(creditNoteId: number) {
    const target = payable[0];
    if (!target) return;
    setApplyingCreditNoteId(creditNoteId);
    try {
      const updated = await applyCreditNote.mutateAsync({
        id: creditNoteId,
        data: { periodId: target.period.id },
      });
      setPaidOverrides((prev) => {
        const next = new Map(prev);
        next.set(updated.id, updated.amountPaid);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      queryClient.invalidateQueries({ queryKey: getListAccountCreditNotesQueryKey(accountId) });
      toast({ title: "Credit note applied toward rent due." });
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      toast({
        title: "Couldn't apply the credit note.",
        description: code === "nothing_due" ? "Nothing due on this period." : undefined,
        variant: "destructive",
      });
    } finally {
      setApplyingCreditNoteId(null);
    }
  }

  // Recompute the bank-amount default whenever the collectible total shrinks
  // (e.g. after a deposit-apply / credit-note-apply) and the operator hasn't
  // gone partial yet.
  useEffect(() => {
    if (!partial) setAmount(String(totalToCollect > 0 ? totalToCollect : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalToCollect]);

  async function onProof(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadPaymentProof(file);
      if (r.ok) {
        setProofUrl(r.url);
        toast({ title: "Proof of payment uploaded." });
      } else {
        toast({
          title: "Couldn't upload proof",
          description: r.message || `Upload failed (HTTP ${r.status}).`,
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Couldn't upload proof",
        description: (err as Error)?.message ?? "Network error — check your connection.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    const thisPayment = Number(amount);
    if (!Number.isFinite(thisPayment) || thisPayment <= 0) {
      toast({ title: "Enter a valid amount.", variant: "destructive" });
      return;
    }
    if (payable.length === 0) {
      toast({ title: "All periods in this deal are already paid." });
      onClose();
      return;
    }
    const paymentDate = new Date(when).toISOString();
    try {
      // Sequential fill: each allocated line gets its `give` (rent-then-deposit
      // split server-side via bankAmount when it carries a deposit; otherwise a
      // plain amountPaid running total).
      for (const a of allocation) {
        const data =
          a.line.deposit > 0
            ? {
                bankAmount: a.give,
                paymentMode: mode,
                paymentDate,
                paymentProofUrl: proofUrl,
              }
            : {
                amountPaid: round2((a.line.period.amountPaid ?? 0) + a.give),
                paymentMode: mode,
                paymentDate,
                paymentProofUrl: proofUrl,
              };
        await markPaid.mutateAsync({ periodId: a.line.period.id, data });
      }
      // Partial → record the next due date for the balance (shown on every
      // period the deal touched, matching the single dialog's reminder write).
      if (balanceAfter > 0 && nextDue) {
        for (const a of allocation) {
          await updateReminder.mutateAsync({ periodId: a.line.period.id, data: { reminderDate: nextDue } });
        }
      }
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      toast({
        title:
          balanceAfter > 0
            ? "Partial payment recorded."
            : totalDeposit > 0
              ? "Deal settled — rent + deposit recorded."
              : "Deal settled — marked fully paid.",
      });
      onClose();
    } catch (err) {
      toast({ title: "Couldn't record the payment.", description: apiErrorDetail(err), variant: "destructive" });
    }
  }

  async function markUnpaid() {
    try {
      // Server clears the ledger for each period (same path as the single dialog).
      for (const l of lines) {
        if ((l.period.amountPaid ?? 0) > 0) {
          await markPaid.mutateAsync({ periodId: l.period.id, data: { amountPaid: 0 } });
        }
      }
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      toast({ title: "Marked unpaid." });
      onClose();
    } catch {
      toast({ title: "Couldn't update.", variant: "destructive" });
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record payment — combined deal</DialogTitle>
          <DialogDescription>
            {group.members.length} space types · one payment settles the whole deal
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Per-space-type breakdown — informational only, NOT an allocation UI.
              Shows exactly what the lump sum covers (rent + any one-time
              deposit) so the operator can see the grand total's composition. */}
          <div className="space-y-1 rounded-md border px-3 py-2 text-sm">
            {lines.map((l) => (
              <div key={l.period.id} className="flex justify-between text-muted-foreground">
                <span className="truncate">
                  {(l.period.planName ?? "Plan").replace(/_/g, " ")}
                  {(l.period.quantity ?? 1) > 1 ? ` × ${l.period.quantity}` : ""}
                  {l.rentRemaining <= 0 && l.deposit <= 0 ? " · paid" : ""}
                </span>
                <span className="tabular-nums">
                  {formatINR(round2(l.rentRemaining + l.deposit))}
                </span>
              </div>
            ))}
            {totalDeposit > 0 && (
              <div className="flex justify-between border-t pt-1 text-[11px] text-muted-foreground">
                <span>of which security deposit (one-time, held)</span>
                <span className="tabular-nums">{formatINR(totalDeposit)}</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-1 font-semibold">
              <span>Total to collect</span>
              <span className="tabular-nums">{formatINR(totalToCollect)}</span>
            </div>
          </div>

          {/* Quick task 9 — apply a Held security deposit toward the deal's
              rent due. Applies toward the FIRST payable period (sequential),
              reducing the combined total to collect. */}
          {payable.length > 0 && heldDeposits.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2">
              <p className="text-xs font-medium text-sky-900">Apply a held deposit toward this due</p>
              {heldDeposits.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground">
                    Held deposit — {formatINR(d.amount)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={applyDeposit.isPending}
                    onClick={() => onApplyDeposit(d.id)}
                  >
                    {applyingDepositId === d.id ? "Applying…" : `Apply ₹${Math.round(d.amount)}`}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Quick task 9 — apply a WHOLE Issued/Adjusted credit note toward
              the deal's rent due. No partial apply (operator-locked). */}
          {payable.length > 0 && creditNotes.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-violet-200 bg-violet-50/60 px-3 py-2">
              <p className="text-xs font-medium text-violet-900">Apply a credit note toward this due</p>
              {creditNotes.map((cn) => (
                <div key={cn.id} className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground truncate">
                    {cn.creditNoteNumber} — {formatINR(cn.totalReversed)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={applyCreditNote.isPending}
                    onClick={() => onApplyCreditNote(cn.id)}
                  >
                    {applyingCreditNoteId === cn.id ? "Applying…" : `Apply ₹${Math.round(cn.totalReversed)}`}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <p className="text-sm font-medium">Partial payment</p>
              <p className="text-[11px] text-muted-foreground">
                {partial
                  ? "Enter the bank amount received"
                  : `Receiving the full ${formatINR(totalToCollect)} together`}
              </p>
            </div>
            <Switch
              checked={partial}
              onCheckedChange={(v) => {
                setPartial(v);
                setAmount(v ? "" : String(totalToCollect > 0 ? totalToCollect : 0));
              }}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Bank amount received (₹)</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={!partial}
              placeholder={partial ? "Amount credited to bank" : ""}
              className="h-9"
            />
            {/* Live sequential-allocation preview — same pure walk as save(). */}
            {allocation.length > 0 && (
              <div className="text-[11px] text-muted-foreground space-y-0.5">
                {allocation.map((a) => (
                  <div key={a.line.period.id} className="flex justify-between">
                    <span className="truncate">
                      → {(a.line.period.planName ?? "Plan").replace(/_/g, " ")}
                      {a.line.deposit > 0 ? " (rent + deposit)" : ""}
                    </span>
                    <span className="tabular-nums">{formatINR(a.give)}</span>
                  </div>
                ))}
              </div>
            )}
            {payable.length > allocation.length && entered > 0 && (
              <p className="text-[11px] text-amber-700">
                Unallocated — nothing left for {payable.length - allocation.length} space type
                {payable.length - allocation.length === 1 ? "" : "s"}.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Balance after this: {formatINR(balanceAfter)}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Payment mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => (
                    <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date &amp; time</Label>
              <Input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Proof of payment</Label>
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm" disabled={uploading}>
                <label className="cursor-pointer">
                  {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
                  {proofUrl ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => onProof(e.target.files?.[0])}
                  />
                </label>
              </Button>
              {proofUrl && (
                <a href={proofUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">
                  View uploaded proof
                </a>
              )}
            </div>
          </div>

          {/* Partial → when's the remaining balance due. */}
          {balanceAfter > 0 && (
            <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
              <Label className="text-xs">
                Next due date — balance of {formatINR(balanceAfter)}
              </Label>
              <Input
                type="date"
                value={nextDue}
                onChange={(e) => setNextDue(e.target.value)}
                className="h-9"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {anyPaid && (
            <Button variant="ghost" size="sm" className="text-destructive sm:mr-auto" onClick={markUnpaid} disabled={markPaid.isPending}>
              Mark unpaid
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={markPaid.isPending || uploading || payable.length === 0 || !(Number(amount) > 0)}>
            {markPaid.isPending ? "Saving…" : payable.length === 0 ? "All paid" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VisitorsList({ accountId: _accountId }: { accountId: number }) {
  // No visitors table in the schema as of 2026-06-10. Surfacing the gap
  // explicitly so the operator knows what's missing rather than a generic
  // empty state.
  return (
    <div className="space-y-3 pt-4">
      <div className="rounded-md border bg-muted/30 p-6 text-center">
        <p className="text-sm font-medium">Visitor log — coming soon</p>
        <p className="text-xs text-muted-foreground mt-1">
          The visitors table isn&apos;t wired up yet. Once a check-in /
          guest-pass module ships, the people who met with this account
          will show up here with their visit date + purpose.
        </p>
      </div>
    </div>
  );
}

// ── Main detail page ──────────────────────────────────────────────────────

export default function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const accountId = Number(id);

  const { data: account, isLoading: accountLoading } = useGetAccount(accountId);

  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const qc = useQueryClient();

  if (accountLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-md" />
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        Account not found.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top action row — visible always, even with Basic Details collapsed */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <h1 className="text-2xl font-semibold">{account.legalName}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {/* One badge: a company reads "Company"; an individual reads
                Member / Walk-in by its kind. */}
            <Badge variant="outline" className="capitalize">
              {account.type === "company"
                ? "Company"
                : account.kind === "walk_in"
                  ? "Walk-in"
                  : "Member"}
            </Badge>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setBookingOpen(true)}>
            <CalendarPlus className="mr-1.5 h-4 w-4" />
            New Booking
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-1.5 h-4 w-4" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMergeOpen(true)}>
            <GitMerge className="mr-1.5 h-4 w-4" />
            Merge
          </Button>
        </div>
      </div>

      {/* Basic Details — collapsible. Defaults closed so the tabs are
          the primary surface once an operator knows the account. */}
      <Accordion type="single" collapsible defaultValue="basic">
        <AccordionItem value="basic" className="rounded-md border px-3">
          <AccordionTrigger className="hover:no-underline py-3">
            <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Basic Details
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 gap-3 pb-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  GST Treatment
                </p>
                <p className="font-medium">{gstTreatmentLabel(account.gstTreatment)}</p>
              </div>
              {account.gstin && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    GSTIN
                  </p>
                  <p className="font-mono text-xs">{account.gstin}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Place of Supply
                </p>
                <p className="font-medium">{account.placeOfSupply}</p>
              </div>
              {account.billingEmail && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Billing Email
                  </p>
                  <p className="font-medium truncate">{account.billingEmail}</p>
                </div>
              )}
              {account.billingPhone && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Billing Phone
                  </p>
                  <p className="font-medium">{account.billingPhone}</p>
                </div>
              )}
              {account.city && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    City / State
                  </p>
                  <p className="font-medium">
                    {account.city}
                    {account.state ? `, ${account.state}` : ""}
                  </p>
                </div>
              )}
              {account.billingAddress && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Billing Address
                  </p>
                  <p className="font-medium">{account.billingAddress}</p>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Tabs — in-house accounts have no membership plan, so the Plan tab is
          hidden and the view defaults to Transactions (F&B etc.). */}
      <Tabs defaultValue={account.isInternal ? "transactions" : "plan"}>
        <TabsList>
          {!account.isInternal && <TabsTrigger value="plan">Plan</TabsTrigger>}
          <TabsTrigger value="booking">Booking</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="visitors">Visitors</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="communication">Communication</TabsTrigger>
        </TabsList>

        {!account.isInternal && (
          <TabsContent value="plan">
            <Card>
              <CardContent className="pt-4">
                <MembershipsList accountId={accountId} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="booking">
          <Card>
            <CardContent className="pt-4">
              <BookingsList
                accountId={accountId}
                onNewBooking={() => setBookingOpen(true)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members">
          <Card>
            <CardContent className="pt-4">
              <ContactsList accountId={accountId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attendance">
          <Card>
            <CardContent className="pt-4">
              <AttendanceList accountId={accountId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardContent className="pt-4">
              <DocumentsList
                accountId={accountId}
                accountType={account.type}
                gstTreatment={account.gstTreatment}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visitors">
          <Card>
            <CardContent className="pt-4">
              <VisitorsList accountId={accountId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <TransactionsList accountId={accountId} />
        </TabsContent>
        <TabsContent value="communication">
          <CommunicationTab accountId={accountId} />
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <AccountForm
        open={editOpen}
        account={account}
        onClose={() => setEditOpen(false)}
      />

      {/* Merge dialog */}
      <MergeDialog
        open={mergeOpen}
        targetAccountId={accountId}
        onClose={() => setMergeOpen(false)}
      />

      {/* Account-scoped booking sheet */}
      <NewBookingSheet
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        accountId={accountId}
        accountLabel={account.legalName}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["accounts"] });
          // Refresh the Booking tab: bookings list + complimentary hours.
          qc.invalidateQueries({
            queryKey: ["/api/operations/member-charges"],
          });
          qc.invalidateQueries({
            queryKey: ["/api/operations/member-entitlements"],
          });
        }}
      />
    </div>
  );
}

/** Account-level Order Receipt — one per account per month, consolidating all active plans. */
function OrderReceiptDialog({
  accountId,
  open,
  onClose,
}: {
  accountId: number;
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: receipt, isLoading } = useGetAccountOrderReceipt(accountId, {
    query: {
      enabled: open,
      queryKey: getGetAccountOrderReceiptQueryKey(accountId),
    },
  });
  const send = useSendAccountOrderReceipt();

  const grandTotal = Number(receipt?.grandTotal ?? 0);
  const [received, setReceived] = useState<number>(0);
  useEffect(() => {
    setReceived(Number(receipt?.receivedAmount ?? 0));
  }, [receipt?.receiptNumber, receipt?.receivedAmount]);
  const pending = Math.max(0, Math.round((grandTotal - received) * 100) / 100);
  const intraState = receipt != null && Number(receipt.igstTotal ?? 0) === 0;
  const [pendingMode, setPendingMode] = useState<"send" | "download" | null>(null);

  async function onSend() {
    setPendingMode("send");
    try {
      const res = await send.mutateAsync({
        id: accountId,
        data: { receivedAmount: received },
      });
      qc.invalidateQueries({ queryKey: getGetAccountOrderReceiptQueryKey(accountId) });
      toast({
        title: res.delivered ? "Order receipt sent on WhatsApp." : "Receipt PDF generated.",
        description: res.delivered
          ? undefined
          : res.pdfUrl
            ? "WhatsApp not delivered — use the PDF link."
            : "WhatsApp not delivered.",
      });
    } catch (err) {
      toast({
        title: "Couldn't generate the receipt.",
        description: (err as Error)?.message ?? "Try again.",
        variant: "destructive",
      });
    } finally {
      setPendingMode(null);
    }
  }

  async function onDownload() {
    setPendingMode("download");
    try {
      const res = await send.mutateAsync({
        id: accountId,
        data: { receivedAmount: received, skipWhatsapp: true },
      });
      qc.invalidateQueries({ queryKey: getGetAccountOrderReceiptQueryKey(accountId) });
      if (res.pdfUrl) {
        window.open(res.pdfUrl, "_blank", "noopener,noreferrer");
        toast({ title: "Receipt PDF generated — opened in a new tab." });
      } else {
        toast({ title: "Generated, but no PDF URL was returned.", variant: "destructive" });
      }
    } catch (err) {
      toast({
        title: "Couldn't generate the receipt.",
        description: (err as Error)?.message ?? "Try again.",
        variant: "destructive",
      });
    } finally {
      setPendingMode(null);
    }
  }

  const noPlans = receipt != null && receipt.lines.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Order Receipt
            {receipt?.receiptNumber ? ` · ${receipt.receiptNumber}` : ""}
          </DialogTitle>
          <DialogDescription>
            One receipt per account per month — consolidates all active plans
            (full committed term). Record the received amount, then generate &amp;
            send.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !receipt ? (
          <div className="space-y-2 py-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full rounded" />
            ))}
          </div>
        ) : noPlans ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No active plans on this account — add one via Add Plan first.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Months</TableHead>
                    <TableHead className="text-right">Line total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipt.lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{l.description}</TableCell>
                      <TableCell className="text-right text-sm">{l.quantity}</TableCell>
                      <TableCell className="text-right text-sm">
                        {l.durationMonths ?? "—"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatINR(Number(l.lineTotal))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="ml-auto w-full max-w-xs space-y-0.5 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>{formatINR(Number(receipt.subtotal ?? 0))}</span>
              </div>
              {intraState ? (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>CGST</span>
                    <span>{formatINR(Number(receipt.cgstTotal ?? 0))}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>SGST</span>
                    <span>{formatINR(Number(receipt.sgstTotal ?? 0))}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-muted-foreground">
                  <span>IGST</span>
                  <span>{formatINR(Number(receipt.igstTotal ?? 0))}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1 text-base font-semibold">
                <span>Grand Total</span>
                <span>{formatINR(grandTotal)}</span>
              </div>
            </div>

            <div className="space-y-2 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Generate Receipt</span>
                {receipt.pdfUrl && (
                  <a
                    href={receipt.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> Last PDF
                  </a>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Received / advance (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={received}
                    onChange={(e) => setReceived(Math.max(0, Number(e.target.value) || 0))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Pending</Label>
                  <div className="flex h-9 items-center font-medium tabular-nums">
                    {formatINR(pending)}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button
                  size="sm"
                  onClick={onSend}
                  disabled={send.isPending}
                >
                  {pendingMode === "send" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Generate &amp; Send Receipt (WhatsApp)
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDownload}
                  disabled={send.isPending}
                >
                  {pendingMode === "download" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Generate &amp; Download
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Both generate the Hoblix order-receipt PDF and record the received
                amount. <b>Send</b> WhatsApps it to the billing phone; <b>Download</b>
                opens the PDF in a new tab. Pending = Grand Total − Received.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
